/* eslint-disable import/no-unresolved */
import { AgGridReact } from '@ag-grid-community/react';
import clsx from 'clsx';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { GridChartsModule } from '@ag-grid-enterprise/charts';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { SideBarModule } from '@ag-grid-enterprise/side-bar';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

const modules = [
  ClientSideRowModelModule,
  RowGroupingModule,
  SetFilterModule,
  ServerSideRowModelModule,
  RangeSelectionModule,
  SideBarModule,
  GridChartsModule,
  MenuModule,
  ColumnsToolPanelModule,
  ClipboardModule,
];

import AccessForm from '@/Components/AccessForm/AccessForm';
import SheetLoader from '@/Components/Loader/SheetLoader';
import MetaTag from '@/Components/MetaTag';
import CustomPagination from '@/Components/Spreadsheet/CustomPagination/CustomPagination';
import MobilePagination from '@/Components/Spreadsheet/MobilePagination/MobilePagination';
import SheetAssistant from '@/Components/Spreadsheet/SheetAssistant/SheetAssistant';
import ToolPanel from '@/Components/Spreadsheet/ToolPanel/ToolPanel';
import MobileDrawer from '@/Components/UI/MobileDropMenu/MobileDrawer';
import WithoutPublicAccess from '@/Components/WithoutPublicAccess/WithoutPublicAccess';

import { sidebar } from '@/helpers/agGridConfig';
import { paginationHelper } from '@/helpers/paginationHelper';
import { frameworkComponents as defaultFC } from '@/helpers/spreadsheetHelper';
import {
  addRow as _addRow,
  chartData as _chartData,
  deleteRow as _deleteRow,
  firstRowAsHeader as _firstRowAsHeader,
  flagRows as _flagRows,
  getGridRequestData as _getGridRequestData,
  headerAsFirstRow as _headerAsFirstRow,
  autoGroupColumnDef,
  filterContextMenuAction,
  generateDeletingRowsArray,
  generatePathForNode,
  getDataSource,
  getMainMenuItems,
  getRowClassRules,
  getRowId,
  handleDeleteRows,
  hasNoWritePermission,
  icons,
  modifyRowDataAppearance,
  openFormulaPopover,
  trackMixpanelColumnVisibility,
} from '@/helpers/spreadsheetHelper';

import { areArraysEqual } from '@/helpers/expandedNodesUtils';

import { usePreviousColumnDefs } from '@/hooks/usePreviousColumnDefs';
import useViewportProvider from '@/hooks/useViewportProvider';
import { StatusCodes } from 'http-status-codes';

import { fetchOpenedFile, setHeaderCommentThreads } from '@/redux/reducers/datasetSlice';
import { showModal } from '@/redux/reducers/modalsSlice';
import { addMixpanelEvent } from '@/redux/reducers/sharedSlice';
import {
  addCollapsedNode,
  addExpandedNode,
  clearClientState,
  initialState,
  removeAggregationFromFilterModel,
  removeCollapsedNode,
  removeExpandedNode,
  removeToast,
  restoreGridState,
  saveClientState,
  setAggregationEndpointValues,
  setClientStateColumnHighlights,
  setColumnDefs,
  setColumnState,
  setCommentCell,
  setEnrichmentCredits,
  setFilterProgressIsRunning,
  setGenerateDeletingRows,
  setGridSidePanel,
  setIsAggregationEndpointValuesLoading,
  setIsInsertingNewColumn,
  setIsMobileSheetAssistantOpen,
  setIsPivotEnabled,
  setIsPreventFiltersCall,
  setIsRequestingRows,
  setPageNumber,
  setPivotColumnHeaders,
  setProperMobileDrawerContent,
  setReduxFilterModelAggregationsCnf,
  setReduxFilterModelCnf,
  setRefreshCommentThreads,
  setSelectedRow,
  setShownToasts,
  setSortModel,
  updateSortModel,
} from '@/redux/reducers/spreadsheetSlice';

import {
  datasetSliceName,
  enrichmentTemplatesSliceName,
  modalsSliceName,
  sharedSliceName,
  spreadsheetSliceName,
  userDataSliceName,
} from '@/redux/constants';
import {
  AGGREGATION,
  CHANGED_COLUMN_POSITION,
  COLUMN_MANAGER,
  currentFileErrorTexts,
  EVENT_NAME_GIGASHEET_FILTER_PROGRESS,
  FILE,
  FILL_SHARE_EMAIL_PARAM,
  MODAL_COMMENTS,
  MODAL_CREATE_ACCOUNT,
  MODAL_DELETE_ROWS,
  MODAL_FIND_AND_REPLACE,
  MODAL_GENERAL_UPGRADE,
  MODAL_MAKE_COPY,
  MODAL_PASTE_FROM_CONTEXT_MENU,
  MODAL_REQUIRE_ACCOUNT,
  MODAL_SHARE_FILE,
  MODAL_UPGRADE_ACCOUNT,
  OPEN_SHARE_POPUP_PARAM,
  PERMISSION_READ,
  PERMISSION_WRITE,
  QUERY_PARAM_COMMENT_THREAD,
  QUERY_SELECTOR_FORMULA_PANEL,
  SHEET_ASSISTANT,
  SIDE_PANEL_ASSISTANT_PANEL,
  SIDE_PANEL_COLUMN_PANEL,
  SIDE_PANEL_ROWS_PANEL,
  SIDE_PANEL_INFO_PANEL,
  SIDE_PANEL_TOGGLE,
  TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
  TOAST_TYPE_ERROR,
  upgradeAccountTypes,
  URL_DATASET,
  userPermissionsKey,
} from '@/Utils/constants';

import { get, headers, post, put } from '@/Utils/API';
import columnMenuStyling from '@/Utils/columnMenuStyling';
import { getFileUuidFromPath } from '@/Utils/getFileUuidFromPath';
import { getFilterType } from '@/Utils/getFilterType';
import getFlagSubMenu from '@/Utils/getFlagSubMenu';
import { getSelectionRanges } from '@/Utils/getSelectionRanges';
import getTopRangeRowsIndexAndRowCount from '@/Utils/getTopRangeRowsIndexAndRowCount';
import { mapUserPermissions } from '@/Utils/userConversion/userConversion';
import { useAuth0 } from '@auth0/auth0-react';

import { fileSVG, flagsSVG, functionsSVG } from '@/Assets/icons';

import CustomColumnManager from '@/Components/CustomColumnManager/CustomColumnManager';
import FormulaPopoverButton from '@/Components/Spreadsheet/FormulaPopoverButton';
import useJobStatusPolling from '@/hooks/useJobStatusHook';
import useKeyPress from '@/hooks/useKeyPress';
import getAddRowsSubMenu from '@/Utils/getAddRowsSubMenu';

import { getClientState } from '@/Utils/ClientStateCommon';
import { handleUnauthorizedResponseAndReload } from '@/Utils/commonUtils';
import { parseRowCount } from '@/Utils/DatasetTree/parseRowCount';
import {
  AG_GRID_AUTO_COLUMN,
  ROW_NUMBER_COLUMN_ID,
  ROW_NUMBER_COLUMN_NAME,
} from '@/Utils/gridConstants';
import { isValidEmail } from '@/Utils/isValidEmail';
import { parseFileSize } from '@/Utils/parseFileSize';
import {
  checkCanAnalyzeJson,
  getWebsocketHostname,
  checkIsJSONSheet,
  checkIsMRFSheet,
  isGigasheetOwner,
} from '@/Utils/utils';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-alpine.css';
import './index.scss';

import { RenameColumnPopover } from '@/Components/Modals/RenameColumn/RenameColumnPopover';
import ShowToast from '@/Components/Toast/showToastTemplate';
import useAggregationUpdater from '@/hooks/useAggregationUpdater';
import useAuth0SilentRefresh from '@/hooks/useAuth0SilentRefresh';
import { usePrevious } from '@/hooks/usePrevious';
import { _processDataFromClipboard } from '@/pages/SpreadsheetPage/PasteUtils/PasteUtils';
import {
  setExcludedBlankEnrichRowCount,
  setIsLoadingExcludeBlanksEnrich,
} from '@/redux/reducers/enrichmentTemplates';
import { Mutex } from 'async-mutex';
import { CustomColumnHeaderContextMenu } from './CustomHeaderContextMenu';
import { useShowPreviewModeUpsellModal } from '@/hooks/useShowPreviewModeUpsellModal';
import DotsLoader from '@/Components/Loader/DotsLoader';
import { FILE_TYPE_EXTERNAL } from '@/Utils/fileConstants';
import FilterProgressOverlay from '@/pages/SpreadsheetPage/FilterProgressOverlay';

const SpreadsheetPage = ({ navigationProps }) => {
  const { isAuthenticated, logout } = useAuth0();
  const { silentRefresh } = useAuth0SilentRefresh();
  const dispatch = useDispatch();
  const showPreviewModeUpsellModal = useShowPreviewModeUpsellModal();
  const { currentFile, isPreviewMode, filesSizePercentage } = useSelector(
    (state) => state[datasetSliceName]
  );
  const { user, isViewerAnalyst } = useSelector((state) => state[userDataSliceName]);
  const {
    toasts,
    errorInfo,
    pageNumber,
    clientState,
    isDeletingRows,
    isDeletingColumns,
    isRequestingRows,
    totalNumberOfRows,
    paginationPageSize,
    isGridStateRestored,
    isInsertingNewColumn,
    filteredNumberOfRows,
    totalGroupCount,
    userClosedUpgradeRequiredModal,
    columnDefs: reduxColDefs,
    defaultColDef: reduxDefaultColDefs,
    runDeletingRows,
    mobileDrawerContent,
    refreshCommentThreads,
    commentCell,
    customColumnHeaderContextMenuPosition,
    isCustomColumnHeaderContextMenuOpen,
    customContextMenuColumn,
    columnHighlights,
    isRenamePopoverOpen,
    renamePopoverCoordinates,
    isPreventFiltersCall,
    filterProgressIsRunning,
  } = useSelector((state) => state[spreadsheetSliceName]);
  const { isLeftSidebarOpened, isRanByCypress } = useSelector((state) => state[sharedSliceName]);
  const isIframe = window.location !== window.parent.location;
  const { modalsQueue } = useSelector((state) => state[modalsSliceName]);
  const { isExcludeBlankValuesFromEnrichmentRun, enrichSelectedColumns } = useSelector(
    (state) => state[enrichmentTemplatesSliceName]
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const sharedFileWithPermission_READ =
    currentFile?.isFileShared && currentFile?.availablePermissions?.includes(PERMISSION_READ);

  const viewOnlyAccess =
    currentFile?.isFileShared &&
    currentFile?.availablePermissions?.includes(PERMISSION_READ) &&
    !currentFile?.availablePermissions?.includes(PERMISSION_WRITE);

  const [agGrid, setAgGrid] = useState(null);
  const [loadingCommentThreads, setLoadingCommentThreads] = useState(false);
  const [progress, setProgress] = useState(null);
  const [isUpdatingModel, setIsUpdatingModel] = useState(false);
  const [isDataRendered, setIsDataRendered] = useState(false);
  const [headersWithGroupFunc, setHeadersWithGroupFunc] = useState([]);
  const [cellComments, setCellComments] = useState(null);
  const [pageRowNumbers, setPageRowNumbers] = useState([]);
  const updateTimeoutRef = useRef(null);
  const prevGroupedNodeStateRef = useRef(clientState.groupedNodeState);
  const prevIsDeletingRows = usePrevious(isDeletingRows);
  const modelUpdateDebounceTime = 1000;
  const pasteMutex = useRef(new Mutex()).current;
  const isInitialRender = useRef(true);
  const websocket = useRef(null);
  const prevCellCommentsRef = useRef(null);
  const progressTotalRowsRef = useRef(0);
  const progressTotalBytesRef = useRef(0);

  // MRF file is a machine-readable file for US healthcare price transparency
  const isMRFSheet = checkIsMRFSheet(currentFile?.metadata?.ParserUsed);
  const isJSONSheet = checkIsJSONSheet(currentFile?.metadata?.ParserUsed);
  const canAnalyzeJson = checkCanAnalyzeJson(user?.userProperties);

  const revenueBaseAccess = !!user?.userProperties?.revenueBaseAccess && viewOnlyAccess;
  const limitedAnalystMenu = isViewerAnalyst || revenueBaseAccess;
  const isLiveQuery = currentFile?.metadata?.Type === FILE_TYPE_EXTERNAL;

  useEffect(() => {
    let reconnectionAttempts = 0;
    const maxReconnectionAttempts = 5;
    const reconnectionDelay = 2000;
    const maxReconnectionDelay = 30000;
    const webSocketUrl = `${getWebsocketHostname()}/sheet/${getFileUuidFromPath()}/ws`;

    const parseCellComments = (message) => {
      if (Array.isArray(message?.cell_comments)) {
        const validComments = message.cell_comments.every(
          (comment) =>
            typeof comment === 'object' &&
            comment !== null &&
            'row' in comment &&
            'column' in comment
        );

        if (validComments) {
          setCellComments(message.cell_comments);
        } else {
          console.error('cell comments are not valid');
        }
      } else {
        console.error('cell_comments property is missing or not an array');
      }
    };

    const connectWebSocket = () => {
      websocket.current = new WebSocket(webSocketUrl);

      websocket.current.onopen = () => {
        reconnectionAttempts = 0;
        const requestHeaders = headers();
        websocket.current.send(JSON.stringify(requestHeaders));
        webSocketSendSheetStateChangedMessage();
      };

      websocket.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          parseCellComments(message);
        } catch (e) {
          console.error('Error parsing JSON:', e);
        }
      };

      websocket.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      websocket.current.onclose = (event) => {
        if (!event.wasClean && reconnectionAttempts < maxReconnectionAttempts) {
          setTimeout(() => {
            reconnectionAttempts++;
            connectWebSocket();
          }, Math.min(reconnectionDelay * 2 ** reconnectionAttempts, maxReconnectionDelay));
        }
      };
    };

    connectWebSocket();

    return () => {
      if (websocket.current) {
        websocket.current.close();
      }
    };
  }, []);

  const webSocketSendSheetStateChangedMessage = () => {
    if (websocket.current && websocket.current.readyState === WebSocket.OPEN) {
      const message = JSON.stringify({
        handle: getFileUuidFromPath(),
        action: 'sheetStateChanged',
        rowNumbers: pageRowNumbers,
        filterModel: clientState?.filterModel,
        pageNumber: agGrid?.api?.paginationGetCurrentPage(),
        paginationPageSize: agGrid?.api?.paginationGetPageSize(),
      });
      websocket.current.send(message);
    }
  };

  useEffect(() => {
    const onEvent = (e) => {
      try {
        const p = e.detail;
        if (p.Rows === 0 && p.Bytes === 0) return;
        progressTotalRowsRef.current += p.Rows;
        progressTotalBytesRef.current += p.Bytes;
        setProgress({
          PartRows: p.Rows,
          PartBytes: p.Bytes,
          TotalRows: progressTotalRowsRef.current,
          TotalBytes: progressTotalBytesRef.current,
          Elapsed: p.Elapsed,
        });
      } catch (e) {
        console.error('Error parsing JSON:', e);
      }
    };

    window.addEventListener(EVENT_NAME_GIGASHEET_FILTER_PROGRESS, onEvent);

    return () => {
      window.removeEventListener(EVENT_NAME_GIGASHEET_FILTER_PROGRESS, onEvent);
    };
  }, []);

  const formatProgressLine = ({ PartRows, PartBytes, TotalRows, TotalBytes, Elapsed }) => {
    const rowsM = TotalRows / 1e6;
    const seconds = Elapsed / 1e9;
    const rpsM = seconds > 0 ? PartRows / seconds / 1e6 : 0;
    return `Processing: ${rowsM.toFixed(2)} million rows (${rpsM.toFixed(2)} million rows/s.)`;
  };

  const resetFilterProgress = () => {
    setProgress(null);
    progressTotalRowsRef.current = 0;
    progressTotalBytesRef.current = 0;
  };

  useEffect(() => {
    if (!filterProgressIsRunning) {
      resetFilterProgress();
    }
  }, [filterProgressIsRunning]);

  useEffect(() => {
    if (cellComments === null) return;
    if (!pageRowNumbers || pageRowNumbers?.length <= 0) return;

    const filterCommentsByPage = (comments, pageData) =>
      comments.filter((comment) => pageData.includes(comment.row));

    const generateCommentCells = (comments) => [
      ...new Set(comments.map((comment) => `${comment.row}_${comment.column}`)),
    ];

    const areCommentsSame = (arr1, arr2) => {
      if (arr1.length !== arr2.length) return false;
      const sortedArr1 = [...arr1].sort();
      const sortedArr2 = [...arr2].sort();
      return sortedArr1.every((value, index) => value === sortedArr2[index]);
    };

    const currentPageComments = filterCommentsByPage(cellComments, pageRowNumbers);

    if (prevCellCommentsRef.current === null) {
      prevCellCommentsRef.current = currentPageComments;
      return;
    }

    const currentPageCommentCells = generateCommentCells(currentPageComments);
    const prevPageCommentsCells = generateCommentCells(prevCellCommentsRef.current);

    if (!areCommentsSame(currentPageCommentCells, prevPageCommentsCells)) {
      agGrid?.api?.refreshServerSide();
    }

    prevCellCommentsRef.current = currentPageComments;
  }, [
    cellComments,
    clientState?.filterModel,
    agGrid?.api?.paginationGetCurrentPage(),
    agGrid?.api?.paginationGetPageSize(),
    agGrid?.api?.paginationGetTotalPages(),
  ]);

  useEffect(() => {
    const pivotColumnHeaders = agGrid?.api?.getPivotResultColumns()?.map((col) => {
      return col.parent.groupId;
    });
    dispatch(setPivotColumnHeaders(pivotColumnHeaders));
  }, [agGrid?.api?.getPivotResultColumns()]);

  useEffect(() => {
    webSocketSendSheetStateChangedMessage();
  }, [
    clientState?.filterModel,
    agGrid?.api?.paginationGetCurrentPage(),
    agGrid?.api?.paginationGetPageSize(),
    agGrid?.api?.paginationGetTotalPages(),
    pageRowNumbers,
  ]);

  const getPageRowNumbers = () => {
    if (!agGrid?.api) return;
    const currentPage = agGrid.api.paginationGetCurrentPage();
    const pageSize = agGrid.api.paginationGetPageSize();
    const totalPages = agGrid.api.paginationGetTotalPages();
    const isLastPage = currentPage === totalPages - 1;
    const allRowNodes = [];
    agGrid.api.forEachNode((node) => allRowNodes.push(node));
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    const currentPageRowNumbers = allRowNodes
      .filter((node) => node.rowIndex >= startIndex && node.rowIndex < endIndex)
      .map((node) => node?.data?.A)
      .sort();
    if (
      (currentPageRowNumbers.length !== pageSize && !isLastPage) ||
      currentPageRowNumbers.some((el) => !el)
    ) {
      return;
    }
    if (!isEqual(currentPageRowNumbers, pageRowNumbers)) {
      setPageRowNumbers(currentPageRowNumbers);
    }
  };

  const applyGroupedNodeState = (agGrid, clientState) => {
    if (!clientState.groupedNodeState || Object.keys(clientState.groupedNodeState).length === 0)
      return;

    agGrid.api.forEachNode((node) => {
      const nodePath = generatePathForNode(node);
      const state = clientState.groupedNodeState[node.field];

      if (state) {
        const isNodeInExpandedNodes = state.expandedNodes.some((path) =>
          areArraysEqual(nodePath, path)
        );
        const isNodeInCollapsedNodes = state.collapsedNodes.some((path) =>
          areArraysEqual(nodePath, path)
        );

        let shouldExpand;

        if (state.defaultExpanded) {
          shouldExpand = !isNodeInCollapsedNodes;
        } else {
          shouldExpand = isNodeInExpandedNodes;
        }

        if (node.expanded !== shouldExpand) {
          node.setExpanded(shouldExpand);
        }
      }
    });
  };

  const handleNodeStateUpdate = useCallback(
    (node, expanded) => {
      const path = generatePathForNode(node);
      const { field } = node;

      if (expanded) {
        if (clientState.groupedNodeState?.[field]?.defaultExpanded) {
          flushSync(() => dispatch(removeCollapsedNode({ field, path })));
        } else {
          flushSync(() => dispatch(addExpandedNode({ field, path })));
        }
      } else {
        if (clientState.groupedNodeState?.[field]?.defaultExpanded) {
          flushSync(() => dispatch(addCollapsedNode({ field, path })));
        } else {
          flushSync(() => dispatch(removeExpandedNode({ field, path })));
        }
      }
    },
    [clientState.groupedNodeState, dispatch]
  );

  const [prevFilesSizePercentage, setPrevFilesSizePercentage] = useState(
    filesSizePercentage || null
  );

  const [showUserGuidingSurvey, setShowUserGuidingSurvey] = useState(true);

  useJobStatusPolling(currentFile?.metadata.FileUuid, agGrid?.api.refreshServerSide);

  const { isMobileScreen, screenWidth } = useViewportProvider();

  const openCommentTimeout = 2000;

  const [shouldDisableActions, setShouldDisableActions] = useState(false);
  const isAggregationAllowed = useRef();
  const isEmailOrLinkedInUrlPresent = useRef();
  const columnHighlightsRef = useRef(null);
  const prevColumnStateRef = useRef();
  const ungroupedSortModelRef = useRef([]);
  const [isRemoveColumnHighlighting, setIsRemoveColumnHighlighting] = useState(false);

  useEffect(() => {
    setShouldDisableActions(isInsertingNewColumn || isDeletingRows || isDeletingColumns);
    if ((!isInsertingNewColumn || !isDeletingRows || !isDeletingColumns) && agGrid) {
      agGrid?.api?.refreshServerSide();
    }
  }, [isInsertingNewColumn, isDeletingRows, isDeletingColumns]);

  // this USEEFFECT not connected with aggregation
  // here we get row count without blank values for Enrichment row count
  useEffect(() => {
    if (!agGrid || !isExcludeBlankValuesFromEnrichmentRun) return;

    const getExcludedBlankRowsCount = async (columnsIds = []) => {
      const fileUuid = getFileUuidFromPath();
      const flattenedColumnsIds = columnsIds.flat();

      dispatch(setIsLoadingExcludeBlanksEnrich(true));
      const filterStructureNotBlankType = flattenedColumnsIds.map((el) => {
        return {
          colId: el.colId,
          filterType: el.filterType,
          isCaseSensitive: false,
          type: 'isNotBlank',
        };
      });

      const ungroupedAggregations = agGrid.api
        .getColumnState()
        ?.filter((el) => el.colId !== ROW_NUMBER_COLUMN_ID && el.aggFunc)
        .map((el) => ({
          id: el.colId,
          field: el.colId,
          displayName: agGrid.api.getColumnDef(el.colId).headerName,
          aggFunc: el.aggFunc,
        }));

      const aggregationPayload = {
        ...agGrid.api.getGridOption('serverSideDatasource')?.state,
        sortModel: clientState.sortModel,
        ungroupedAggregations,
        filterModel: {
          ...clientState.filterModel,
          _cnf_: [...(clientState.filterModel?._cnf_ || []), filterStructureNotBlankType],
        },
      };
      try {
        dispatch(setIsLoadingExcludeBlanksEnrich(true));
        const response = await post(`${FILE}/${fileUuid}/${AGGREGATION}`, aggregationPayload);
        if (response.status !== StatusCodes.OK) return;
        const responseDataResults = response.results;
        const excludedBlanksRowCount = responseDataResults.reduce(
          (max, obj) => (obj.value > max ? obj.value : max),
          responseDataResults[0].value
        );
        dispatch(setExcludedBlankEnrichRowCount(excludedBlanksRowCount ?? filteredNumberOfRows));
      } catch (e) {
        console.error(e);
      } finally {
        dispatch(setIsLoadingExcludeBlanksEnrich(false));
      }
    };
    getExcludedBlankRowsCount(enrichSelectedColumns);
  }, [isExcludeBlankValuesFromEnrichmentRun]);

  useEffect(() => {
    columnHighlightsRef.current = columnHighlights;
  }, [columnHighlights]);

  const groupedColumnsCount = useMemo(() => {
    return clientState.columnState?.reduce((acc, state) => {
      if (state.rowGroup) ++acc;
      return acc;
    }, 0);
  }, [clientState]);

  const cellHasComment = (params) => {
    try {
      const cellColId = params.colDef.colId;
      const metadataJson = params?.data?.metadata;
      if (metadataJson && metadataJson !== '') {
        const { threads: commentThreads } = JSON.parse(metadataJson);
        if (
          commentThreads &&
          commentThreads.some((commentThread) => commentThread.column === cellColId)
        ) {
          return true;
        }
      }
    } catch (e) {
      console.error(e);
    }
    return false;
  };

  const isCellHighlighted = (params) => {
    return (
      columnHighlightsRef?.current?.[params?.colDef?.colId] &&
      columnHighlightsRef?.current?.[params?.colDef?.colId]?.values?.[params?.data?.['A']]
    );
  };

  const isCellEditable = (params, colId, fieldType) => {
    return (
      !params.node.isRowPinned() ||
      !colId === ROW_NUMBER_COLUMN_ID ||
      !getFilterType(!fieldType) === 'date'
    );
  };

  const cellValueFormatter = (params) => {
    let value;
    const currency = currentFile?.ColumnInfo?.[params?.colDef?.colId]?.currency?.code;
    if (currency && (params?.value || params?.value === 0)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(params.value);
    } else {
      value = params?.value;
    }

    return value;
  };

  const columnDefs = useMemo(() => {
    return reduxColDefs.map((colDef) => {
      return {
        ...colDef,
        enableCellChangeFlash: true,
        cellClassRules: {
          'comment-cell': (params) => cellHasComment(params),
          'highlighted-cell': (params) => isCellHighlighted(params),
        },
        editable: (params) =>
          isCellEditable(params, colDef.colId, colDef.fieldType) && !limitedAnalystMenu,

        cellRendererSelector: (params) => {
          const cellValue = params.value;
          if (!isEmailOrLinkedInUrlPresent?.current) {
            const regex = /https?:\/\/(www.)?linkedin.com/;
            const isLinkedInUrl = cellValue && regex.test(cellValue);
            if (isValidEmail(cellValue) || isLinkedInUrl) {
              isEmailOrLinkedInUrlPresent.current = true;
              window.userGuiding?.previewGuide(67610, {
                checkHistory: true,
              });
              return;
            }
          }
          if (params?.colDef.fieldType === 'MarkdownString' && !params?.node?.rowPinned) {
            return { component: 'markdownString' };
          }

          if (params?.node?.rowPinned) {
            return { component: 'customPinnedRowRenderer' };
          } else if (
            agGrid?.api?.getColumnState().reduce((acc, state) => {
              if (state.rowGroup) ++acc;
              return acc;
            }, 0)
          ) {
            if (
              params.colDef.colId !== AG_GRID_AUTO_COLUMN &&
              !params?.column.rowGroupActive &&
              params?.node?.allChildrenCount > 0 &&
              params.colDef.headerName !== ROW_NUMBER_COLUMN_NAME
            ) {
              return { component: 'aggFunctionSelector' };
            }
          }
          return undefined;
        },
        valueFormatter: (params) => cellValueFormatter(params),
      };
    });
  }, [reduxColDefs, groupedColumnsCount, currentFile]);

  const defaultColDef = useMemo(() => {
    return {
      ...reduxDefaultColDefs,
      enableCellChangeFlash: true,
      editable: true,
      cellRendererSelector: (params) => {
        if (params.value === null) {
          params.value = '(empty)';
        }
      },
      // This is where we wrap the column header with the date function in the Row Group Panel. For example timestamp becomes year(timestamp).
      headerValueGetter: (p) => {
        const { location, column, colDef } = p;
        const { headerName, fieldType } = colDef;
        try {
          if (
            location === 'columnDrop' &&
            column.isRowGroupActive() &&
            fieldType === 'DateTime64' &&
            headersWithGroupFunc.some((e) => headerName === e.header)
          ) {
            return (
              headersWithGroupFunc.find((e) => headerName === e.header)?.groupFunc +
              `(${headerName})`
            );
          }
        } catch (e) {
          console.error(e);
        }
        return headerName;
      },
    };
  }, [reduxDefaultColDefs, headersWithGroupFunc]);

  const gridRef = useRef();
  const gridWrapperRef = useRef();
  const draggingRef = useRef({});
  const isMountedRef = useRef(false);

  const previousColumnDefs = usePreviousColumnDefs(columnDefs, isRequestingRows);

  /* getters */

  const getContextMenuItems = (params) => {
    const MENU_ITEM_NAME = {
      CUT: 'Cut',
      COPY: 'Copy',
      PASTE: 'Paste',
      COMMENT: 'Comment',
      FLAG_AS: 'Flag As',
      ADD_ROW: 'Add Row',
      ADD_ROWS: 'Add Rows',
      DELETE_ROW: 'Delete row',
      DELETE_ROWS: 'Delete rows',
      FILTER_TO_THIS: 'Filter to this',
      COPY_WITH_HEADERS: 'Copy with Headers',
      FILTER_TO_EXCLUDE: 'Filter to exclude this',
      DELETE_ROWS_IN_GROUP: 'Delete all rows in group',
      CHART: 'Chart Data',
    };
    if (params.node === null) return null;
    const ranges = params.api.getCellRanges();
    const { rowCount: selectedRowCount } = getTopRangeRowsIndexAndRowCount(ranges);
    const shouldOneRowBeDeleted =
      ranges.length === 1 && ranges[0].startRow.rowIndex === ranges[0].endRow.rowIndex;
    let { defaultItems } = params;
    if (!groupedColumnsCount) {
      defaultItems = params.defaultItems.filter((item) => !['copyWithGroupHeaders'].includes(item));
    }
    const contextMenuItems = [...defaultItems].filter(
      (param) => !['export', 'paste'].includes(param)
    );
    if (params.node.rowPinned !== 'bottom') {
      contextMenuItems.push(
        {
          name: MENU_ITEM_NAME.FLAG_AS,
          icon: `<img src=${flagsSVG.FlagIcon} alt="flag as" class="context-menu-flag-icon"/>`,
          subMenu: getFlagSubMenu(
            params,
            flagRows,
            dispatch,
            user,
            currentFile,
            isPreviewMode,
            showPreviewModeUpsellModal
          ),
        },
        {
          name: selectedRowCount > 1 ? MENU_ITEM_NAME.ADD_ROWS : MENU_ITEM_NAME.ADD_ROW,
          icon: `<img src=${fileSVG.AddNewIcon} alt="Add Row" class="context-menu-add-row-icon"/>`,
          subMenu: getAddRowsSubMenu({
            user,
            dispatch,
            currentFile,
            agGrid,
            _addRow,
            showModal,
            selectedRowCount,
            isPreviewMode,
            showPreviewModeUpsellModal,
          }),
        },
        {
          name:
            params.node.group && params.value === params.node.key
              ? MENU_ITEM_NAME.DELETE_ROWS_IN_GROUP
              : shouldOneRowBeDeleted
              ? MENU_ITEM_NAME.DELETE_ROW
              : MENU_ITEM_NAME.DELETE_ROWS,
          action: user?.is_anonymous
            ? () => flushSync(() => dispatch(showModal({ name: MODAL_CREATE_ACCOUNT })))
            : hasNoWritePermission(currentFile)
            ? () => flushSync(() => dispatch(showModal({ name: MODAL_MAKE_COPY })))
            : isPreviewMode
            ? () =>
                flushSync(() => {
                  showPreviewModeUpsellModal();
                })
            : () => {
                if (!params.node.group) {
                  return flushSync(() => dispatch(setGenerateDeletingRows(true)));
                } else {
                  const rowForRemove = filterContextMenuAction(params, 'equalsAny');
                  const rowsToDelete = `${
                    getGridRequestData.rowGroupCols.length ? '-matching-filter' : ''
                  }`;
                  const requestBodyForRemove = {
                    ...getGridRequestData,
                    filterModel: {
                      _cnf_: [rowForRemove],
                    },
                  };
                  deleteRow(rowsToDelete, requestBodyForRemove);
                }
              },
          icon: `<img src=${functionsSVG.DeleteRowIcon} alt="delete-row" class="context-menu-delete-icon" id="delete-row"/>`,
          disabled:
            shouldDisableActions ||
            (params.node.group && !params.value) ||
            (params.node.group && params.value !== params.node.key),
        }
      );
    }
    if (
      !groupedColumnsCount ||
      params.column.colId === AG_GRID_AUTO_COLUMN ||
      params.node.level === groupedColumnsCount
    ) {
      contextMenuItems.push(
        {
          name: MENU_ITEM_NAME.FILTER_TO_THIS,
          action: () => {
            const filterToIncludeThis = filterContextMenuAction(params, 'equalsAny');
            if (isPreviewMode && clientState?.filterModel?._cnf_?.length >= 1) {
              return showPreviewModeUpsellModal(
                'Add Additional Filter Conditions on the Premium Plan'
              );
            }
            flushSync(() =>
              dispatch(
                setReduxFilterModelCnf([
                  ...(clientState?.filterModel?._cnf_ || []),
                  filterToIncludeThis,
                ])
              )
            );
            flushSync(() =>
              dispatch(
                addMixpanelEvent({
                  eventName: 'Filter Used',
                  eventProps: {
                    Location: 'row context menu',
                    current_filename: currentFile?.metadata?.FileName,
                  },
                  userIncrementName: '# of filter use',
                })
              )
            );
          },
          icon: `<img src=${functionsSVG.FilterToOnlyIcon} alt="filter" class="context-menu-filter-icon" id="includes"/>`,
          disabled: shouldDisableActions,
        },
        {
          name: MENU_ITEM_NAME.FILTER_TO_EXCLUDE,
          action: () => {
            if (isPreviewMode && clientState?.filterModel?._cnf_?.length >= 1) {
              return showPreviewModeUpsellModal(
                'Add Additional Filter Conditions on the Premium Plan'
              );
            }
            const filterToExcludeThis = filterContextMenuAction(params, 'notEqualsAny');
            flushSync(() =>
              dispatch(
                setReduxFilterModelCnf([
                  ...(clientState?.filterModel?._cnf_ || []),
                  filterToExcludeThis,
                ])
              )
            );
            flushSync(() =>
              dispatch(
                addMixpanelEvent({
                  eventName: 'Filter Used',
                  eventProps: {
                    Location: 'row context menu',
                    current_filename: currentFile?.metadata?.FileName,
                  },
                  userIncrementName: '# of filter use',
                })
              )
            );
          },
          icon: `<img src=${functionsSVG.FilterToExcludeIcon} alt="filter" class="context-menu-filter-icon" id="excludes"/>`,
          disabled: shouldDisableActions,
        },
        {
          name: MENU_ITEM_NAME.COMMENT,
          action: user?.is_anonymous
            ? () => flushSync(() => dispatch(showModal({ name: MODAL_CREATE_ACCOUNT })))
            : hasNoWritePermission(currentFile)
            ? () => flushSync(() => dispatch(showModal({ name: MODAL_MAKE_COPY })))
            : isPreviewMode
            ? () =>
                flushSync(() => {
                  return showPreviewModeUpsellModal();
                })
            : () => {
                const { rowIndex, column } = gridRef.current.api.getFocusedCell();
                const rowNumber =
                  gridRef.current.api.getDisplayedRowAtIndex(rowIndex).data[ROW_NUMBER_COLUMN_ID];
                dispatch(
                  showModal({
                    name: MODAL_COMMENTS,
                    props: {
                      column: column.colId,
                      row: rowNumber,
                    },
                  })
                );
              },
          icon: `<img src=${functionsSVG.ChatTextIcon} alt="comment" class="context-menu-chat-text-icon" id="excludes"/>`,
          disabled: shouldDisableActions || (params.node.group && params.value === params.node.key),
        }
      );
    }

    // Override copy and copyWithHeaders to send Mixpanel events.
    const copyIndex = contextMenuItems.findIndex((item) => item === 'copy');
    const copyWithHeadersIndex = contextMenuItems.findIndex((item) => item === 'copyWithHeaders');
    const { columnCount, rowCount } = getSelectionRanges(params.api.getCellRanges());

    if (copyIndex !== -1) {
      const copyAction = () => {
        let modalName = null;

        if (user && user.is_anonymous) {
          modalName = MODAL_CREATE_ACCOUNT;
        } else if (isPreviewMode) {
          return showPreviewModeUpsellModal();
        } else {
          modalName = null;
        }

        if (modalName) {
          dispatch(
            showModal({
              name: modalName,
              props: {
                columnId: params?.column?.colDef?.colId,
                columnName: params?.column?.colDef?.headerName,
              },
            })
          );
          return;
        }

        params.api.copySelectedRangeToClipboard(params);
        flushSync(() => {
          dispatch(
            addMixpanelEvent({
              eventName: 'Copy Data (client)',
              eventProps: { columnCount, rowCount },
            })
          );
        });
      };
      contextMenuItems[copyIndex] = {
        name: MENU_ITEM_NAME.COPY,
        action: copyAction,
        icon: `<img src=${fileSVG.CopyIcon} alt="copy" class="context-menu-copy-icon" id="context-copy"/>`,
      };
    }
    if (copyWithHeadersIndex !== -1) {
      const copyWithHeaderAction = () => {
        let modalName = null;

        if (user && user.is_anonymous) {
          modalName = MODAL_CREATE_ACCOUNT;
        } else if (isPreviewMode) {
          return showPreviewModeUpsellModal();
        } else {
          modalName = null;
        }

        if (modalName) {
          dispatch(
            showModal({
              name: modalName,
              props: {
                columnId: params?.column?.colDef?.colId,
                columnName: params?.column?.colDef?.headerName,
              },
            })
          );
          return;
        }

        params.api.copySelectedRangeToClipboard({ includeHeaders: true, ...params });
        flushSync(() => {
          dispatch(
            addMixpanelEvent({
              eventName: 'Copy Data - Headers (client)',
              eventProps: { columnCount, rowCount },
            })
          );
        });
      };
      contextMenuItems[copyWithHeadersIndex] = {
        name: MENU_ITEM_NAME.COPY_WITH_HEADERS,
        action: copyWithHeaderAction,
        icon: `<img src=${fileSVG.CopyIcon} alt="copy-with-headers" class="context-menu-copy-icon" id="context-copy-with-headers"/>`,
      };
    }

    contextMenuItems.splice(copyWithHeadersIndex + 1, 0, {
      name: MENU_ITEM_NAME.PASTE,
      icon: `<img src=${fileSVG.PasteIcon} alt="Paste" class="context-menu-paste-icon"/>`,
      action: user?.is_anonymous
        ? () => flushSync(() => dispatch(showModal({ name: MODAL_CREATE_ACCOUNT })))
        : viewOnlyAccess
        ? () => flushSync(() => dispatch(showModal({ name: MODAL_MAKE_COPY })))
        : isPreviewMode
        ? () =>
            flushSync(() => {
              return showPreviewModeUpsellModal();
            })
        : () =>
            flushSync(() =>
              dispatch(
                showModal({
                  name: MODAL_PASTE_FROM_CONTEXT_MENU,
                  props: {
                    pasteFromContextMenu: true,
                  },
                })
              )
            ),
    });

    const VIEWER_ANALYST_MODE_EXCLUDED_ITEMS = [
      MENU_ITEM_NAME.CUT,
      MENU_ITEM_NAME.COPY,
      MENU_ITEM_NAME.PASTE,
      MENU_ITEM_NAME.COMMENT,
      MENU_ITEM_NAME.ADD_ROW,
      MENU_ITEM_NAME.ADD_ROWS,
      MENU_ITEM_NAME.DELETE_ROW,
      MENU_ITEM_NAME.DELETE_ROWS,
      MENU_ITEM_NAME.COPY_WITH_HEADERS,
      MENU_ITEM_NAME.DELETE_ROWS_IN_GROUP,
      MENU_ITEM_NAME.FLAG_AS,
    ];

    const analystModeContextMenuItems = contextMenuItems.filter(
      (item) =>
        typeof item === 'object' &&
        item !== null &&
        item.hasOwnProperty('name') &&
        !VIEWER_ANALYST_MODE_EXCLUDED_ITEMS.includes(item.name)
    );

    if (limitedAnalystMenu) return analystModeContextMenuItems;
    return contextMenuItems;
  };

  /* getters */

  /* listeners */
  const onGridReady = async (_agGrid) => {
    try {
      const metadata = await getClientState(getFileUuidFromPath());
      dispatch(setClientStateColumnHighlights(metadata?.ClientState?.columnHighlights || null));
      const dataSource = getDataSource(_agGrid, silentRefresh, logout);
      _agGrid.api.setGridOption('pinnedBottomRowData', [{}]);
      _agGrid.api.setGridOption('serverSideDatasource', dataSource);

      _agGrid.api.autoSizeAllColumns();
      if (isIframe) {
        _agGrid.api.closeToolPanel();
      }

      setAgGrid(_agGrid);

      const isAggregationsPrevented =
        isPreviewMode && !currentFile?.isFileShared && currentFile?.metadata?.FileSize;
      if (isAggregationsPrevented) return;
      if (isInitialRender.current) {
        if (currentFile?.metadata?.FileRows < 100000000) {
          isInitialRender.current = false;
        }
      }
      dispatch(setIsAggregationEndpointValuesLoading(true));

      let columnStateAgg;
      if (new Date(metadata.LastAccessed).getTime() < 0) {
        const columnState = _agGrid.api.getColumnState().map((el, idx) => {
          if (idx === 1 && el.colId !== ROW_NUMBER_COLUMN_ID && el.colId !== AG_GRID_AUTO_COLUMN) {
            return {
              ...el,
              aggFunc:
                getFilterType(_agGrid.api.getColumnDef(el.colId).fieldType) === 'number'
                  ? 'sum'
                  : 'row count',
            };
          } else {
            return { ...el, hide: el.colId === ROW_NUMBER_COLUMN_ID ? true : false };
          }
        });
        columnStateAgg = columnState;
        dispatch(setColumnState(columnStateAgg));
      } else {
        columnStateAgg = metadata.ClientState.columnState;
        dispatch(setColumnState(columnStateAgg));
      }

      if (columnStateAgg.some((el) => el.aggFunc)) {
        const data = {
          ..._agGrid.api.getGridOption('serverSideDatasource')?.state,
          sortModel: clientState?.sortModel,
          filterModel: clientState?.filterModel,
          ungroupedAggregations: columnStateAgg
            .filter(
              (el) =>
                el.colId !== AG_GRID_AUTO_COLUMN &&
                el.colId !== ROW_NUMBER_COLUMN_ID &&
                el.id !== AG_GRID_AUTO_COLUMN
            )
            .map((el) => ({
              id: el.colId,
              field: el.colId,
              displayName: _agGrid.api.getColumnDef(el.colId)?.headerName,
              aggFunc: el.aggFunc,
            })),
        };
        const fileId = getFileUuidFromPath();
        const response = await post(`${FILE}/${fileId}/${AGGREGATION}`, data);
        dispatch(setAggregationEndpointValues(response.results));
      }
      dispatch(setIsAggregationEndpointValuesLoading(false));
    } catch (error) {
      console.error(error);
    } finally {
      if (window.location.host.includes('localhost')) {
        window.grid = _agGrid;
      }
      dispatch(setIsAggregationEndpointValuesLoading(false));
    }
  };

  const removeLastGroup = () => {
    const rowGroupColumns = agGrid.api.getRowGroupColumns();
    if (rowGroupColumns.length > 0) {
      const lastGroupColumn = rowGroupColumns[rowGroupColumns.length - 1];
      agGrid.api.removeRowGroupColumn(lastGroupColumn.getColId());
    }
  };

  const onGroup = (event) => {
    if (isPreviewMode && agGrid.api.getRowGroupColumns().length > 1) {
      showPreviewModeUpsellModal('Add Additional Group Conditions on the Premium Plan');
      return removeLastGroup();
    }
    const currentlyGrouped = event?.api?.getRowGroupColumns().length > 0;
    if (event.source === 'toolPanelUi') {
      flushSync(() =>
        flushSync(() =>
          dispatch(
            addMixpanelEvent({
              eventName: 'Group By Used',
              eventProps: {
                Location: 'columns tool panel',
                current_filename: currentFile?.metadata?.FileName,
              },
              userIncrementName: '# of filter use',
            })
          )
        )
      );
      const columnsState = event?.api?.getColumnState();

      const updatedState = reduxColDefs.map((el) => {
        const isColumnGrouped = columnsState.find((column) => {
          return column.colId === el.colId;
        });

        if (isColumnGrouped) {
          return { ...el, ...isColumnGrouped };
        }

        return { ...el, hide: el.rowGroup ? false : el.hide, rowGroup: false, rowGroupIndex: null };
      });

      flushSync(() => dispatch(setColumnState(updatedState)));
    }

    const aggregatedColumns = event.api.getValueColumns();
    const isAtLeastOneSortedAggColumn = aggregatedColumns.find(({ colId }) =>
      clientState.sortModel.some((sortedColumn) => sortedColumn.colId === colId)
    );

    const lastGrouppedColumn = event.columns[event.columns.length - 1];

    const firstLeftColumn = columnDefs.find((columnDef) => {
      const isColumnAggregated = event.columns.some(({ colId }) => colId === columnDef.colId);
      if (columnDef.colId === ROW_NUMBER_COLUMN_ID) return false;
      if (isColumnAggregated) return false;
      return columnDef.colId !== lastGrouppedColumn?.colId;
    });

    const isAggregatedFirstLeftColumn = aggregatedColumns.find(
      ({ colId }) => firstLeftColumn?.colId === colId
    );

    const groupedColumnsAmount =
      event.source === 'toolPanelUi' ? groupedColumnsCount + 1 : groupedColumnsCount;

    const isSortingApplied = !!clientState?.sortModel?.length;

    if (!event.columns.length) {
      flushSync(() => dispatch(saveClientState({ groupedNodeState: {} })));
      flushSync(() => dispatch(setReduxFilterModelAggregationsCnf([])));
      flushSync(() => dispatch(setSortModel([])));
    } else {
      if (clientState.isPivotEnabled) return;
      if (!isAggregatedFirstLeftColumn && firstLeftColumn) {
        agGrid.api.addValueColumn(firstLeftColumn.colId);
        event.api.addValueColumn(firstLeftColumn.colId, 'row count');
      }
      if (
        event.columns.length === 1 &&
        groupedColumnsAmount === 1 &&
        !isAtLeastOneSortedAggColumn &&
        firstLeftColumn &&
        !isSortingApplied
      ) {
        flushSync(() =>
          dispatch(updateSortModel([{ colId: firstLeftColumn.colId, sort: 'desc' }]))
        );
      }
    }

    if (!currentlyGrouped) {
      // when going from grouped to ungrouped state we should restore the ungrouped sort model
      dispatch(setSortModel(ungroupedSortModelRef.current));
    }
  };

  useEffect(() => {
    if (agGrid?.api && clientState?.sortModel) {
      const currentlyGrouped = agGrid?.api?.getRowGroupColumns().length > 0;
      const currentSortModel = clientState?.sortModel;
      if (!currentlyGrouped) {
        ungroupedSortModelRef.current = currentSortModel;
      }
    }
  }, [clientState.sortModel]);

  const onRowGroupOpened = (event) => {
    const { node, expanded } = event;

    const path = generatePathForNode(node);
    const { field } = node;

    // Avoid unnecessary updates
    const groupedNodeState = clientState.groupedNodeState?.[field];
    if (groupedNodeState) {
      const isCurrentlyExpanded = groupedNodeState.defaultExpanded
        ? !groupedNodeState.collapsedNodes.some((p) => areArraysEqual(p, path))
        : groupedNodeState.expandedNodes.some((p) => areArraysEqual(p, path));

      if (isCurrentlyExpanded === expanded) return;
    }

    handleNodeStateUpdate(node, expanded);
  };

  const onCellClick = (event) => {
    const { rowIndex, data } = event;
    if (event.node.level === groupedColumnsCount && event.rowPinned !== 'bottom') {
      flushSync(() =>
        dispatch(
          setSelectedRow({
            rowIndex,
            rowData: modifyRowDataAppearance(data),
          })
        )
      );
    }
  };

  const onCellFocus = (event) => {
    const { rowIndex } = event;
    agGrid.api.forEachNode((node) => {
      if (node.rowIndex !== rowIndex) return;
      flushSync(() =>
        dispatch(
          setSelectedRow({
            rowIndex,
            rowData: modifyRowDataAppearance(node.data),
          })
        )
      );
    });
  };

  const removePivotSortEntries = () => {
    const sortModelWithoutPivotSorts = clientState?.sortModel.filter(
      (entry) => !entry.pivotFieldGroupId
    );
    dispatch(setSortModel(sortModelWithoutPivotSorts));
  };

  const onPivot = (event) => {
    if (event.api.isPivotMode()) {
      flushSync(() => dispatch(setIsPivotEnabled(true)));
      return;
    }
    removePivotSortEntries();
    flushSync(() => dispatch(setIsPivotEnabled(false)));
    event.api.setPivotResultColumns(null);
  };

  const goToPage = useCallback(
    (_pageNumber) => {
      prevCellCommentsRef.current = null;
      pageNumber !== _pageNumber && flushSync(() => dispatch(setPageNumber(_pageNumber)));
    },
    [pageNumber, dispatch]
  );

  const onPaginationChanged = (e) => {
    goToPage(e.api.paginationGetCurrentPage());
    getPageRowNumbers();
  };

  const onColumnVisible = (event) => {
    trackMixpanelColumnVisibility();

    if (event.source === 'api') return;
    const { api } = event;
    const colsState = api.getColumnState();
    flushSync(() => dispatch(setColumnState(colsState)));
  };

  const onColumnMoved = (event) => {
    if (event?.column && event?.toIndex) {
      const { colId } = event?.column || {};
      const { toIndex } = event;

      const dragStopColIndex = event?.api?.getColumnState()?.find(({ rowGroup }) => rowGroup)
        ? toIndex - 1
        : toIndex;

      draggingRef.current.dragStartColId = colId;
      draggingRef.current.dragStopColIndex = dragStopColIndex;
    }
  };

  const onDragStopped = async () => {
    if (!draggingRef.current.dragStartColId) return;

    const dragStartColIndex = reduxColDefs.findIndex(
      (column) => column.colId === draggingRef.current.dragStartColId
    );
    const { dragStopColIndex } = draggingRef.current;

    if (dragStopColIndex === dragStartColIndex) return;

    flushSync(() =>
      dispatch(
        setColumnDefs(
          agGrid.api.getColumnDefs().map((colDef) => {
            const correspondingColDefFromRedux = reduxColDefs.find(
              (reduxColDef) => reduxColDef.field === colDef.field
            );
            return correspondingColDefFromRedux;
          })
        )
      )
    );

    const uuid = getFileUuidFromPath();
    const data = {
      col_id: draggingRef.current.dragStartColId,
      from_index: dragStartColIndex,
      to_index: dragStopColIndex,
    };
    await post(`${URL_DATASET}/${uuid}/${CHANGED_COLUMN_POSITION}`, data);

    draggingRef.current.dragStartColId = null;
  };

  const onAggregate = (event) => {
    const { columns: valueColumns } = event;

    const { columnState: prevColumnState } = clientState;

    const prevColumnsWithAggFunc = prevColumnState.filter((el) => {
      return el.aggFunc;
    });

    const validColIds = reduxColDefs.map((col) => col.field);
    const filteredColumnState = agGrid.api
      .getColumnState()
      .filter((col) => validColIds.includes(col.colId));

    flushSync(() => dispatch(setColumnState(filteredColumnState)));
    const pivotCols = agGrid.api.getPivotColumns();

    if (prevColumnsWithAggFunc.length > valueColumns.length) {
      flushSync(() =>
        dispatch(removeAggregationFromFilterModel(valueColumns.map(({ colId }) => colId)))
      );
    }

    flushSync(() =>
      dispatch(
        setColumnDefs(
          reduxColDefs.map((colDef) => {
            const isColumnPivoted = pivotCols.some((pivotCol) => pivotCol.colId === colDef.colId);
            const isColumnAggregated = valueColumns.some(
              (valueCol) => valueCol.colId === colDef.colId
            );
            return {
              ...colDef,
              enablePivot: isColumnAggregated ? false : !pivotCols.length,
              enableValue: !isColumnPivoted,
            };
          })
        )
      )
    );
  };

  const onAddToColumnLabels = ({ columns: pivotCols }) => {
    const currentColumnState = agGrid.api.getColumnState();

    const validColIds = reduxColDefs.map((col) => col.field);

    const filteredColumnState = currentColumnState.filter(({ colId }) =>
      validColIds.includes(colId)
    );

    const updatedColumnDefs = reduxColDefs.map((colDef) => {
      const isPivoted = pivotCols.some(({ colId }) => colId === colDef.field);
      return {
        ...colDef,
        enablePivot: true,
        enableValue: !isPivoted,
      };
    });

    flushSync(() => {
      dispatch(setColumnState(filteredColumnState));
    });
    flushSync(() => {
      dispatch(setColumnDefs(updatedColumnDefs));
    });

    const gridPivotCols = agGrid.api.getPivotColumns();

    if (gridPivotCols.length === 0) {
      agGrid.api.setPivotResultColumns(null);
    }
  };

  const onFirstDataRendered = () => {
    if (isDataRendered === false) {
      setIsDataRendered(true);
    }
    restoreChartModel();
  };

  const onModelUpdated = (e) => {
    if (!agGrid || !groupedColumnsCount) return;

    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    setIsUpdatingModel(true);

    // restore expansion state across model changes
    if (clientState.groupedNodeState && Object.keys(clientState.groupedNodeState).length > 0) {
      applyGroupedNodeState(agGrid, clientState);
    }

    updateTimeoutRef.current = setTimeout(() => {
      setIsUpdatingModel(false);
    }, modelUpdateDebounceTime);
  };

  const onNewColumnsLoaded = (event) => {
    if (!previousColumnDefs.length || previousColumnDefs.length === columnDefs.length) return;

    const newColumnsId = columnDefs.reduce((acc, { colId: id }) => {
      const isNewColumn = !previousColumnDefs.some(({ colId }) => colId === id);
      if (isNewColumn) acc.push(id);
      return acc;
    }, []);

    event.api.autoSizeColumns(newColumnsId);
    const maxWidth = 400;
    newColumnsId.forEach((colId) => {
      event.api.setColumnWidth(colId, maxWidth);
    });
  };

  const onColumnRenamed = ({ api }) => {
    const currentColumns = api.getColumnDefs();

    if (!previousColumnDefs.length) {
      setTimeout(() => {
        api.autoSizeAllColumns(false);
      }, 0);
    }

    if (currentColumns.length === previousColumnDefs.length) {
      let isReorderedColumns = false;
      const renamedHeaders = currentColumns.reduce((acc, el, index) => {
        const { headerName, colId } = el;
        if (colId === previousColumnDefs[index].colId && !isReorderedColumns) {
          if (headerName !== previousColumnDefs[index].headerName) {
            return [...acc, el.colId];
          }
        } else {
          isReorderedColumns = true;
        }
        return acc;
      }, []);

      if (renamedHeaders.length) {
        api.autoSizeColumn(renamedHeaders, false);
      }
    }
  };

  const onChartCreated = () => {
    debouncedSaveChartModel();
  };

  const onChartRangeSelectionChanged = () => {
    debouncedSaveChartModel();
  };

  const onChartOptionsChanged = () => {
    debouncedSaveChartModel();
  };

  const onChartDestroyed = () => {
    flushSync(() => dispatch(setSelectedRow(null)));
    debouncedSaveChartModel();
  };

  const saveChartModel = useCallback(() => {
    try {
      const chartModels = gridRef?.current?.api?.getChartModels() || [];
      queueMicrotask(() =>
        flushSync(() => dispatch(saveClientState({ chartModels: chartModels })))
      );
    } catch (e) {
      ShowToast({
        type: 'error',
        text: 'An error occurred while saving the chart model. Your chart changes may not be saved.',
        errorContext: e,
        fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
      });
    }
  }, [gridRef]);

  const debouncedSaveChartModel = useMemo(() => debounce(saveChartModel, 500), [saveChartModel]);

  const restoreChartModel = useCallback(() => {
    try {
      if (clientState?.chartModels?.length > 0) {
        clientState?.chartModels?.forEach((chartModel) => {
          gridRef.current.api.restoreChart(chartModel);
        });
      }
    } catch (e) {
      ShowToast({
        type: 'error',
        text: 'An error occurred while restoring the chart model. Your chart failed to load.',
        errorContext: e,
        fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
      });
    }
  }, [clientState.chartModels]);

  const handleKeyDown = (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
      event.preventDefault();
      if (!currentFile.WithinQuota || currentFile.OverRowQuota) {
        return flushSync(() => {
          return showPreviewModeUpsellModal();
        });
      }

      flushSync(() =>
        dispatch(showModal({ name: MODAL_FIND_AND_REPLACE, props: { getGridRequestData } }))
      );
    }
  };

  /* listeners */

  /* actions */

  const getGridRequestData = _getGridRequestData(agGrid);

  const freeUserExport = () => {
    if (!agGrid) return;
    agGrid.api?.exportDataAsCsv({
      shouldRowBeSkipped: ({ node }) => node.rowIndex >= 100,
    });
    flushSync(() =>
      dispatch(
        showModal({
          name: MODAL_UPGRADE_ACCOUNT,
          props: { upgradeType: upgradeAccountTypes.export },
        })
      )
    );
    flushSync(() =>
      dispatch(
        addMixpanelEvent({
          eventName: 'File Export',
          eventProps: { 'Export Type': 'Current Page' },
          userIncrementName: '# of exports',
        })
      )
    );
  };

  const flagRows = (params, flag) => _flagRows(params, flag, agGrid);

  const firstRowAsHeader = () => _firstRowAsHeader(dispatch, agGrid, currentFile?.metadata);

  const headerAsFirstRow = () => _headerAsFirstRow(dispatch, agGrid, currentFile?.metadata);

  const chartData = () => _chartData(agGrid);

  const deleteRow = (rowsToDelete, requestBody) => _deleteRow(rowsToDelete, requestBody);

  const getRowNumberInFilterResult = async (rowNumber) => {
    const defaultRowNumberInFilter = 0;
    try {
      const requestAgGrid = gridRef?.current?.api.getGridOption('serverSideDatasource')?.state;
      const handle = getFileUuidFromPath();
      if (requestAgGrid && handle && rowNumber) {
        const response = await post(`file/${handle}/filter/${rowNumber}`, requestAgGrid);
        return response.row_number_in_filter_result;
      }
      return defaultRowNumberInFilter;
    } catch (e) {
      return defaultRowNumberInFilter;
    }
  };

  const scrollToRow = (agGridReact, rowIndex) => {
    try {
      agGridReact.api.ensureIndexVisible(rowIndex - 2);
    } catch (e) {
      console.error(e);
    }
  };

  const openCommentsAndSelectCell = async ({
    agGridReact,
    column,
    row,
    isColumnComment,
    isTimeout = true,
  }) => {
    const isGrouped = agGridReact.api.getRowGroupColumns()?.length > 0;
    if (isGrouped) {
      dispatch(
        showModal({
          name: MODAL_COMMENTS,
          props: {
            column: column,
            row: isColumnComment ? -1 : row,
            isColumnComment: isColumnComment,
            askToResetSheet: isGrouped,
          },
        })
      );
    } else {
      const rowNumberInFilter = await getRowNumberInFilterResult(row);
      const pageIndex = Math.ceil(rowNumberInFilter / paginationPageSize) - 1;

      const showModalAndProperCellSelection = () => {
        if (!isColumnComment) {
          goToPage(pageIndex);
          const rowIndex = rowNumberInFilter - 1;
          scrollToRow(agGridReact, rowIndex);
          const cellRange = {
            rowStartIndex: rowIndex,
            rowEndIndex: rowIndex,
            columnStart: column,
            columnEnd: column,
          };
          agGridReact.api.deselectAll();
          agGridReact.api.clearCellSelection();
          agGridReact.api.addCellRange(cellRange);
        }
        dispatch(
          showModal({
            name: MODAL_COMMENTS,
            props: {
              column: column,
              row: isColumnComment ? -1 : row,
              isColumnComment: isColumnComment,
            },
          })
        );
      };

      if (isTimeout) {
        setTimeout(() => {
          showModalAndProperCellSelection();
        }, openCommentTimeout);
      } else {
        showModalAndProperCellSelection();
      }
    }
  };

  const checkForOpenCommentThreadRequest = async (agGridReact) => {
    try {
      const queryParamCommentThread = searchParams.get(QUERY_PARAM_COMMENT_THREAD);
      searchParams.delete(QUERY_PARAM_COMMENT_THREAD);
      setSearchParams(searchParams);

      if (agGridReact && queryParamCommentThread?.length > 0) {
        const matchResults = /(\w*)_(\d*)/g.exec(queryParamCommentThread);

        if (matchResults.length === 3) {
          const [, column] = matchResults;
          const row = parseInt(matchResults[2]);
          const isColumnComment = row === 0;
          openCommentsAndSelectCell({ agGridReact, column, row, isColumnComment });
        }
      }

      if (agGridReact && commentCell !== null && !queryParamCommentThread) {
        const row = commentCell?.row;
        const column = commentCell?.column;
        const isColumnComment = commentCell?.isColumnComment;
        openCommentsAndSelectCell({ agGridReact, column, row, isColumnComment, isTimeout: false });
        dispatch(setCommentCell(null));
      }
    } catch (e) {
      console.error(e);
    }
  };

  /* actions */

  const autoSizeAll = () => {
    gridRef?.current?.api.autoSizeAllColumns(true);

    flushSync(() => dispatch(setColumnState(agGrid.api.getColumnState())));
  };
  useEffect(() => {
    if (!agGrid || isMobileScreen || !user || user?.is_anonymous) return;
    if (clientState?.sidePanel && clientState.sidePanel.chosenTab === '') {
      const DEFAULT_SIDE_PANEL_TAB =
        isAuthenticated || isRanByCypress ? SIDE_PANEL_ASSISTANT_PANEL : SIDE_PANEL_INFO_PANEL;
      dispatch(
        setGridSidePanel({
          chosenTab: DEFAULT_SIDE_PANEL_TAB,
          closed: false,
        })
      );
      agGrid.api.openToolPanel(DEFAULT_SIDE_PANEL_TAB);
    }
  }, [agGrid, agGrid?.api.getOpenedToolPanel(), agGrid?.api.isToolPanelShowing()]);

  useEffect(() => {
    if (!agGrid || !user || user?.is_anonymous) return;
    if (clientState.sidePanel.chosenTab !== '' && !clientState.sidePanel.closed) {
      agGrid.api.openToolPanel(clientState.sidePanel.chosenTab);
    } else {
      agGrid.api.closeToolPanel();
    }
    dispatch(saveClientState({ sidePanel: clientState?.sidePanel }));
  }, [clientState?.sidePanel, agGrid, dispatch]);

  // This useEffect is used as a helper function when we need to interact with a grid element
  // to which we don't have direct access, and we need to perform some action when clicking on this element.
  useEffect(() => {
    const classToSidePanelMap = {
      'sheet-assistant': SIDE_PANEL_ASSISTANT_PANEL,
      columns_details: SIDE_PANEL_COLUMN_PANEL,
      row_detail: SIDE_PANEL_ROWS_PANEL,
      stats_detail: SIDE_PANEL_INFO_PANEL,
    };

    const onNecessaryElementClick = (event) => {
      const { className, id } = event.target;
      const mainToggle = id === SIDE_PANEL_TOGGLE;

      if (classToSidePanelMap[className] || mainToggle) {
        const chosenTab = !mainToggle
          ? classToSidePanelMap[className]
          : clientState?.sidePanel?.chosenTab;

        const isToolPanelOpen = agGrid.api.isToolPanelShowing();

        dispatch(
          setGridSidePanel({
            chosenTab,
            closed: !isToolPanelOpen,
          })
        );

        if (isToolPanelOpen) {
          agGrid.api.closeToolPanel();
        } else {
          agGrid.api.openToolPanel(chosenTab);
        }
        return;
      }

      if (className === 'ag-side-button-button') {
        const button = event.target.closest('.ag-side-button-button');
        const chosenTab = button?.querySelector('.ag-side-button-icon-wrapper div')?.id;

        if (chosenTab) {
          const isToolPanelOpen = agGrid.api.isToolPanelShowing();

          dispatch(
            setGridSidePanel({
              chosenTab,
              closed: !isToolPanelOpen,
            })
          );

          if (isToolPanelOpen) {
            agGrid.api.closeToolPanel();
          } else {
            agGrid.api.openToolPanel(chosenTab);
          }
        }
      }

      if (
        (className === 'row_detail' || className === 'ag-side-button-button') &&
        agGrid.api.getOpenedToolPanel() === SIDE_PANEL_ROWS_PANEL
      ) {
        const { firstRowPerPage } = paginationHelper(agGrid);
        agGrid.api.setFocusedCell(
          firstRowPerPage?.rowIndex,
          agGrid.api.getAllDisplayedColumns()[0]
        );
        queueMicrotask(() =>
          flushSync(() =>
            dispatch(
              setSelectedRow({
                rowIndex: firstRowPerPage.rowIndex,
                rowData: modifyRowDataAppearance(firstRowPerPage.data),
              })
            )
          )
        );
        return firstRowPerPage?.setSelected(true);
      }
      if (
        className === 'ag-icon ag-icon-tree-open' ||
        className === 'ag-icon ag-icon-tree-closed'
      ) {
        isAggregationAllowed.current = true;
      } else {
        isAggregationAllowed.current = false;
      }
    };

    document.addEventListener('click', onNecessaryElementClick);
    return () => {
      document.removeEventListener('click', onNecessaryElementClick);
    };
  }, [agGrid, dispatch]);

  useEffect(() => {
    const isOpenSharePopup = searchParams.get(OPEN_SHARE_POPUP_PARAM);
    const accessRequestUserEmail = searchParams.get(FILL_SHARE_EMAIL_PARAM);

    if (isOpenSharePopup === 'true' && agGrid) {
      queueMicrotask(() =>
        flushSync(() =>
          dispatch(showModal({ name: MODAL_SHARE_FILE, props: { accessRequestUserEmail } }))
        )
      );
      setSearchParams({});
    }
  }, [agGrid]);

  useEffect(() => {
    setTimeout(
      () => {
        const requestAgGrid = gridRef?.current?.api.getGridOption('serverSideDatasource')?.state;
        if (requestAgGrid) {
          checkForOpenCommentThreadRequest(gridRef.current);
        }
      },
      commentCell ? 0 : 2000
    );
  }, [gridRef?.current?.api.getGridOption('serverSideDatasource')?.state, commentCell]);

  const getGroupsKeys = (node) => {
    const keys = [];
    while (node.parent) {
      keys.push(node.key);
      node = node.parent;
    }
    return keys.reverse();
  };

  const getSheetCommentThreads = () => {
    if (gridRef?.current?.api) {
      const isGroupedData = gridRef.current.api.getRowGroupColumns().length > 0;
      if (isGroupedData) {
        const expandedNodes = [];
        gridRef?.current?.api.forEachNode((node) => {
          if (node.expanded) {
            expandedNodes.push(node);
          }
        });
        expandedNodes.forEach((node) => {
          const route = getGroupsKeys(node);
          gridRef.current.api.refreshServerSide({ route: route });
        });
      } else {
        gridRef.current.api.refreshServerSide();
      }
    }
  };

  const _refreshCommentThreads = async () => {
    try {
      if (!loadingCommentThreads) {
        setLoadingCommentThreads(true);
        getSheetCommentThreads();
        await getClientState(getFileUuidFromPath()).then((metadata) => {
          dispatch(setHeaderCommentThreads(metadata?.Comments?.threads));
        });
      }
    } catch (e) {
      setLoadingCommentThreads(false);
      console.error(e);
    } finally {
      setLoadingCommentThreads(false);
    }
  };

  /*
    If refreshCommentThreads state variable is set to true we refresh the comment threads. This variable is set to true
    in the Comments component when the user creates a new comment thread.
  */
  useEffect(() => {
    if (refreshCommentThreads) {
      _refreshCommentThreads();
      dispatch(setRefreshCommentThreads(false));
    }
  }, [refreshCommentThreads]);

  useEffect(() => {
    if (shouldOpenModalUpgradeAccount() && !shouldOpenModalUpgradeRequired()) {
      queueMicrotask(() =>
        flushSync(() =>
          dispatch(
            showModal({
              name: MODAL_GENERAL_UPGRADE,
              props: { premiumFeature: 'fileSize' },
            })
          )
        )
      );
      setPrevFilesSizePercentage(filesSizePercentage);
    }
  }, [filesSizePercentage, isLeftSidebarOpened, prevFilesSizePercentage, currentFile, dispatch]);

  useEffect(() => {
    if ((isMRFSheet || isJSONSheet) && !canAnalyzeJson && !isGigasheetOwner(currentFile)) {
      queueMicrotask(() =>
        flushSync(() =>
          dispatch(
            showModal({
              name: MODAL_GENERAL_UPGRADE,
              props: { premiumFeature: 'jsonAnalyst' },
            })
          )
        )
      );
    }
  }, [currentFile, dispatch]);

  const getSearchedColumns = (searchValue) =>
    agGrid.api
      .getColumnDefs()
      .filter((column) => column.headerName.toLowerCase().includes(searchValue))
      .map(({ colId, headerName }) => ({ colId, headerName }));

  const showAllColumns = () => {
    const searchPlaceholderValue = (
      document.querySelector('.ag-text-field-input')?.value || ''
    ).toLowerCase();

    const searchedColumnState = agGrid?.api.getColumnState().map((column) => ({
      ...column,
      hide: getSearchedColumns(searchPlaceholderValue).some(({ colId }) => colId === column.colId)
        ? false
        : column.hide,
    }));

    const newColsState = agGrid?.api.getColumnState().map((column) => ({
      ...column,
      hide: false,
    }));

    const finalColumnState = searchPlaceholderValue.length > 0 ? searchedColumnState : newColsState;

    queueMicrotask(() => flushSync(() => dispatch(setColumnState(finalColumnState))));
  };
  const hideAllColumns = () => {
    const searchPlaceholderValue = (
      document.querySelector('.ag-text-field-input')?.value || ''
    ).toLowerCase();

    const searchedColumnState = agGrid?.api.getColumnState().map((column) => ({
      ...column,
      hide: getSearchedColumns(searchPlaceholderValue).some(({ colId }) => colId === column.colId)
        ? true
        : column.hide,
    }));

    const newColsState = agGrid?.api.getColumnState().map((column) => ({
      ...column,
      hide: true,
    }));

    const finalColumnState = searchPlaceholderValue.length > 0 ? searchedColumnState : newColsState;

    flushSync(() => dispatch(setColumnState(finalColumnState)));
  };

  const toggleColumnsToolPanel = () => {
    const isToolPanelOpen = agGrid.api.isToolPanelShowing();
    if (isToolPanelOpen) {
      agGrid.api.closeToolPanel();
    } else {
      agGrid.api.openToolPanel('columns');
    }
    agGrid.api.deselectAll();
  };

  const toggleToolPanelTooltip = () => {
    const isToolPanelShowing = agGrid.api.isToolPanelShowing();
    const closePanelIcon = document.querySelector('.columns-header-cross');

    if (isToolPanelShowing) {
      closePanelIcon.classList.add('close');
      closePanelIcon.classList.remove('open');
    } else {
      closePanelIcon.classList.add('open');
      closePanelIcon.classList.remove('close');
    }
  };

  const onToolPanelVisibleChanged = () => {
    const isToolPanelOpen = agGrid?.api?.isToolPanelShowing();
    const targetElement = document.querySelector('.columns-header-cross');

    if (targetElement) {
      if (isToolPanelOpen) {
        targetElement.classList.remove('!rotate-180');
        targetElement.classList.remove('!-translate-x-1/2');
        targetElement.classList.remove('!-translate-y-1/2');
      } else {
        targetElement.classList.add('!rotate-180');
        targetElement.classList.add('!-translate-x-1/2');
        targetElement.classList.add('!-translate-y-1/2');
      }
    }
  };

  const handleSpacebarPress = () => {
    const focusedElement = document.activeElement;
    if (
      focusedElement.tagName === 'INPUT' ||
      focusedElement.tagName === 'TEXTAREA' ||
      focusedElement.tabIndex === 0
    ) {
      return;
    }
    if (agGrid) {
      const gridCell = agGrid.api.getFocusedCell();
      agGrid?.api?.startEditingCell({ rowIndex: gridCell.rowIndex, colKey: gridCell.column });
    }
  };

  const handleUserGuidingWorkflow = async () => {
    const fetchPreAnalysisStatus = async () => {
      try {
        const response = await get('user/metadata', true);

        handleUnauthorizedResponseAndReload(response, 'metadata');
        const metadata = await response?.json();

        if (metadata.Success === false) {
          throw new Error(`GET error: ${metadata.Message}`);
        }

        const { feature_preanalysis } = metadata;

        if (user?.[userPermissionsKey]?.length > 0) {
          window.userGuiding?.identify(user.email, {
            email: user.email,
            ...mapUserPermissions(user[userPermissionsKey]),
            feature_preanalysis,
          });
        }
      } catch (err) {
        console.error(`Failed to fetch feature_preanalysis: ${err}`);
        if (user?.[userPermissionsKey]?.length > 0) {
          window.userGuiding?.identify(user.email, {
            email: user.email,
            ...mapUserPermissions(user[userPermissionsKey]),
          });
        }
      }
    };

    if (!user?.is_anonymous) await fetchPreAnalysisStatus();

    if (!user?.is_anonymous && currentFile?.isFileShared) window.userGuiding?.previewGuide(68492);

    const isEmailAddressFieldType = currentFile?.metadata?.FieldsTypes?.some(
      (type) => type === 'EmailAddress'
    );
    const isIPFieldType = currentFile?.metadata?.FieldsTypes.some(
      (type) => type === 'IPv4' || type === 'IPv6'
    );
    const isNotFirstTimeOpened = new Date(currentFile?.metadata?.LastAccessed).getTime() > 0;
    const isNewFile =
      !isNotFirstTimeOpened &&
      (currentFile?.metadata?.Headers?.length > 1000 ||
        currentFile?.metadata?.Headers?.length <= 3);

    if (isNewFile) {
      window.userGuiding?.previewGuide(92289);
    } else if (isEmailAddressFieldType) {
      window.userGuiding?.previewGuide(67610, {
        checkHistory: true,
      });
    } else if (isIPFieldType) {
      window.userGuiding?.previewGuide(67607, {
        checkHistory: true,
      });
    }

    setShowUserGuidingSurvey(false);
  };

  useEffect(() => {
    const columnsHeaderElement = document.querySelector('.ag-column-select-header');
    const searchPlaceholder = document.querySelector('.ag-text-field-input');
    const hideAllColumnsBtn = document.createElement('button');
    const showAllColumnsBtn = document.createElement('button');
    const rightSideBar = document.querySelector('.ag-side-bar');
    const toolPanelBtns = document.querySelector('.ag-side-buttons');
    const columnsVisibilityButtonsWrapper = document.createElement('div');
    const textHideBtn = document.createTextNode('Hide All');
    const textShowBtn = document.createTextNode('Show All');
    if (!agGrid || !columnsHeaderElement || !searchPlaceholder || !toolPanelBtns) return;
    searchPlaceholder.placeholder = 'Search Columns...';
    columnsVisibilityButtonsWrapper.classList.add('columns-visibility-btns-wrapper');

    hideAllColumnsBtn.classList.add('hideAllColumnsBtn');
    hideAllColumnsBtn.append(textHideBtn);
    hideAllColumnsBtn.addEventListener('click', hideAllColumns);

    showAllColumnsBtn.classList.add('showAllColumnsBtn');
    showAllColumnsBtn.append(textShowBtn);
    showAllColumnsBtn.addEventListener('click', showAllColumns);
    columnsVisibilityButtonsWrapper.append(hideAllColumnsBtn);
    columnsVisibilityButtonsWrapper.append(showAllColumnsBtn);

    columnsHeaderElement.append(columnsVisibilityButtonsWrapper);

    const closePanelIcon = document.createElement('div');
    closePanelIcon.setAttribute('id', 'toggle-sidepanel');
    closePanelIcon.classList.add('columns-header-cross');
    closePanelIcon.classList.add('columns-header-cross-tooltip');
    closePanelIcon.classList.add('open');
    closePanelIcon.addEventListener('click', toggleToolPanelTooltip);

    closePanelIcon.setAttribute('data-cy', 'close-columns-tool-panel');
    closePanelIcon.addEventListener('click', toggleColumnsToolPanel);
    toolPanelBtns.append(closePanelIcon);

    if (isMobileExperienceActive) {
      rightSideBar.classList.add('hidden');
      rightSideBar.classList.add('md:flex');
    } else {
      rightSideBar.classList.add('flex');
    }
    return () => {
      closePanelIcon.removeEventListener('click', toggleToolPanelTooltip);
      closePanelIcon.removeEventListener('click', toggleColumnsToolPanel);
      hideAllColumnsBtn.removeEventListener('click', hideAllColumns);
      showAllColumnsBtn.removeEventListener('click', showAllColumns);
    };
  }, [agGrid]);

  useEffect(() => {
    const onMount = async () => {
      const uuid = getFileUuidFromPath();

      const {
        payload: { currentFile },
      } = await dispatch(fetchOpenedFile(uuid));

      if (!currentFile?.metadata) return;
      let { ClientState } = currentFile?.metadata || {};

      if (typeof ClientState === 'string') {
        ClientState = JSON.parse(ClientState);
      }
      if (ClientState) {
        queueMicrotask(() => flushSync(() => dispatch(restoreGridState(ClientState))));
      } else {
        console.error('ClientState is undefined or null');
      }
    };

    onMount();
  }, [dispatch]);

  useEffect(() => {
    if (totalNumberOfRows && showUserGuidingSurvey) {
      handleUserGuidingWorkflow();
    }
  }, [totalNumberOfRows]);

  // we expose cypressMoveColumn to the window obj to later use it inside tests
  useEffect(() => {
    if (!agGrid) return;
    window.cypressMoveColumn = async (colId, toIndex) => {
      const { api } = agGrid || {};
      const uuid = getFileUuidFromPath();

      const fromIndex = api?.getColumnState()?.findIndex((e) => e.colId === colId);
      api.moveColumn(colId, toIndex);

      queueMicrotask(() =>
        flushSync(() =>
          dispatch(
            setColumnDefs(
              agGrid.api.getColumnDefs().map((colDef) => {
                const correspondingColDefFromRedux = reduxColDefs.find(
                  (reduxColDef) => reduxColDef.field === colDef.field
                );
                return correspondingColDefFromRedux;
              })
            )
          )
        )
      );

      const data = {
        col_id: colId,
        from_index: fromIndex,
        to_index: toIndex,
      };
      await post(`${URL_DATASET}/${uuid}/${CHANGED_COLUMN_POSITION}`, data);
    };
  }, [agGrid, dispatch]);

  useLayoutEffect(() => {
    if (!agGrid) return;

    agGrid.api.applyColumnState({ state: clientState.columnState });

    queueMicrotask(() =>
      flushSync(() => dispatch(saveClientState({ columnState: clientState.columnState })))
    );
  }, [clientState.columnState, agGrid, dispatch]);

  useLayoutEffect(() => {
    if (!agGrid || !isDataRendered) return;

    const prevGroupedNodeState = prevGroupedNodeStateRef.current;
    const currentGroupedNodeState = clientState.groupedNodeState;

    // First Load
    if (prevGroupedNodeState && Object.keys(prevGroupedNodeState).length === 0) {
      applyGroupedNodeState(agGrid, clientState);
    }

    if (!isEqual(prevGroupedNodeState, currentGroupedNodeState)) {
      queueMicrotask(() =>
        flushSync(() => dispatch(saveClientState({ groupedNodeState: currentGroupedNodeState })))
      );
      applyGroupedNodeState(agGrid, clientState);

      prevGroupedNodeStateRef.current = currentGroupedNodeState;
    }
  }, [clientState.groupedNodeState, agGrid, isDataRendered]);

  useLayoutEffect(() => {
    if (!agGrid) return;
    if (prevIsDeletingRows === false && isDeletingRows === true) {
      applyGroupedNodeState(agGrid, clientState);
    }
  }, [isDeletingRows, agGrid, clientState, prevIsDeletingRows]);

  useEffect(() => {
    if (!agGrid) return;
    agGrid.api.paginationGoToPage(pageNumber);
  }, [pageNumber, agGrid]);

  useLayoutEffect(() => {
    if (!agGrid) return;
    if (!isPreventFiltersCall) {
      queueMicrotask(() =>
        flushSync(() => dispatch(saveClientState({ filterModel: clientState.filterModel })))
      );

      const dataSource = getDataSource(agGrid, silentRefresh, logout);
      agGrid.api.setGridOption('serverSideDatasource', dataSource);
    }
    dispatch(setIsPreventFiltersCall(false));
  }, [clientState.filterModel, agGrid, dispatch]);

  useLayoutEffect(() => {
    if (!agGrid) return;
    agGrid.api.onSortChanged();

    queueMicrotask(() =>
      flushSync(() => dispatch(saveClientState({ sortModel: clientState.sortModel })))
    );
    const allColumns = agGrid.api.getAllGridColumns();
    const autoCols = allColumns?.filter(
      (col) => !columnDefs.some((def) => def.field === col.getColId())
    );
    autoCols && agGrid.api.refreshServerSide();
  }, [clientState.sortModel, dispatch, agGrid]);

  useEffect(() => {
    queueMicrotask(() => saveClientState({ alwaysConfirm: clientState.alwaysConfirm }));
  }, [clientState.alwaysConfirm]);

  useEffect(() => {
    queueMicrotask(() =>
      saveClientState({ alwaysConfirmChangeCase: clientState.alwaysConfirmChangeCase })
    );
  }, [clientState.alwaysConfirmChangeCase]);

  useEffect(() => {
    queueMicrotask(() =>
      saveClientState({ alwaysConfirmDeleteRow: clientState.alwaysConfirmDeleteRow })
    );
  }, [clientState.alwaysConfirmDeleteRow]);

  useEffect(() => {
    queueMicrotask(() =>
      saveClientState({ alwaysConfirmChangeCase: clientState.alwaysConfirmChangeCase })
    );
  }, [clientState.alwaysConfirmChangeCase]);

  useEffect(() => {
    if (!agGrid) return;
    if (columnDefs.length > previousColumnDefs.length && previousColumnDefs.length) {
      let firstNewColumnId = null;

      const styledColDefs = columnDefs.map((colDef) => {
        const isColumnNew = previousColumnDefs.every(
          (prevColDef) => prevColDef.field !== colDef.field
        );
        if (!isColumnNew) return colDef;
        if (!firstNewColumnId) firstNewColumnId = colDef.field;

        // in case of bugs
        // substitute sort with refreshServerSide
        return {
          ...colDef,
          sort: 'asc',
          enableCellChangeFlash: true,
          cellStyle: () => ({ backgroundColor: 'rgba(58, 200, 212, 0.24)' }),
        };
      });
      agGrid.api.setGridOption('columnDefs', styledColDefs);
      agGrid.api.ensureColumnVisible(firstNewColumnId);

      const timeout = setTimeout(() => {
        setIsRemoveColumnHighlighting(true);
      }, 10000);

      return () => {
        if (!isRequestingRows) return;
        clearTimeout(timeout);
      };
    }
  }, [columnDefs, agGrid, previousColumnDefs, dispatch, isRequestingRows]);

  useEffect(() => {
    if (!agGrid && !isRemoveColumnHighlighting) return;
    if (agGrid && !isRemoveColumnHighlighting) return;
    agGrid.api.setGridOption(
      'columnDefs',
      columnDefs.map((colDef) => ({
        ...colDef,
        sort: undefined,
        cellStyle: () => ({ backgroundColor: 'transparent' }),
      }))
    );
    setIsRemoveColumnHighlighting(false);
  }, [isRemoveColumnHighlighting]);

  useLayoutEffect(() => {
    if (!agGrid) return;

    agGrid.api.setGridOption('pivotMode', clientState.isPivotEnabled);

    queueMicrotask(() =>
      flushSync(() => dispatch(saveClientState({ isPivotEnabled: clientState.isPivotEnabled })))
    );
  }, [clientState.isPivotEnabled, agGrid, dispatch]);

  useEffect(() => {
    const notShownToasts = toasts.filter((toast) => !toast.wasShown);
    if (notShownToasts.length) {
      queueMicrotask(() => flushSync(() => dispatch(setShownToasts(notShownToasts))));
    }

    const resetClientState = () => {
      clearClientState({ sidePanel: clientState.sidePanel });
      agGrid.api.resetColumnState();
      queueMicrotask(() =>
        flushSync(() =>
          dispatch(
            restoreGridState({ ...initialState.clientState, sidePanel: clientState.sidePanel })
          )
        )
      );
    };

    notShownToasts.forEach((toast) => {
      const onClose = () => flushSync(() => dispatch(removeToast(toast.id)));
      const toastText =
        toast.message === TOAST_TEXT_GENERAL_SPREADSHEET_ERROR ? (
          <>
            {TOAST_TEXT_GENERAL_SPREADSHEET_ERROR}{' '}
            <span
              role='button'
              tabIndex={0}
              onKeyDown={() => null}
              onClick={resetClientState}
              style={{ textDecoration: 'underline' }}
            >
              …or try resetting the file
            </span>
          </>
        ) : (
          toast.message
        );
      ShowToast({
        type: toast.type,
        text: toastText,
        errorContext: {
          message: toast.message,
        },
        fileAndFunction: toast.fileAndFunction,
        endpoint: toast.endpoint,
        payload: toast.payload,
        responseStatus: toast.responseStatus,
        other: toast.other,
        contentHeader: toast.contentHeader,
        onClose,
      });
    });
  }, [toasts, dispatch]);

  useLayoutEffect(() => {
    queueMicrotask(() =>
      flushSync(() => dispatch(saveClientState({ groupedNodeState: clientState.groupedNodeState })))
    );
  }, [clientState.groupedNodeState, dispatch]);

  useEffect(() => {
    document.title = currentFile ? currentFile?.metadata?.FileName + ' - Gigasheet' : 'Gigasheet';
  }, [currentFile, dispatch]);

  useEffect(() => {
    if (!agGrid) return;

    if (!filteredNumberOfRows && agGrid.api.getDisplayedRowCount() !== 1) {
      agGrid.api.showNoRowsOverlay();
    } else {
      agGrid.api.hideOverlay();
    }
  }, [agGrid, filteredNumberOfRows]);

  useEffect(() => {
    if (!agGrid || !isDeletingRows) return;

    agGrid.api.refreshServerSide();
  }, [agGrid, isDeletingRows]);

  useEffect(() => {
    if (!agGrid) return;

    const valuesArea = document.querySelector(
      '.ag-column-drop.ag-column-drop-vertical:nth-child(4) span.ag-column-drop-vertical-empty-message'
    );
    if (!valuesArea) return;

    if (!clientState?.columnState) return;

    const { aggregatedColsCount, pivotedColsCount } = clientState.columnState.reduce(
      (acc, curr) => {
        if (curr.pivot) {
          acc.pivotedColsCount++;
        }
        if (curr.aggFunc) {
          acc.aggregatedColsCount++;
        }
        return acc;
      },
      { aggregatedColsCount: 0, pivotedColsCount: 0 }
    );

    const isMissedAggValue =
      clientState.isPivotEnabled && pivotedColsCount && groupedColumnsCount && !aggregatedColsCount;

    if (isMissedAggValue) valuesArea.classList.add('agg-value-missed');
    else if (!isMissedAggValue) valuesArea.classList.remove('agg-value-missed');
  });

  useEffect(() => {
    queueMicrotask(() => saveClientState({ savedFilterHandle: clientState.savedFilterHandle }));
  }, [clientState.savedFilterHandle]);

  useEffect(() => {
    if (!agGrid) return;

    agGrid.api.refreshServerSide();
  }, [agGrid, paginationPageSize]);

  // This useEffect is used to refresh the grid when the user changes the date function (GroupFunc) of a grouped DateTime column in the Group Panel. For example if the user selects Year, Quarter, Month, etc.
  useEffect(() => {
    const currentGroupFunctions = clientState?.columnState?.map((column) => column.groupFunc);
    const previousGroupFunctions = prevColumnStateRef?.current?.map((column) => column.groupFunc);

    const hasPrevColumns = prevColumnStateRef?.current?.length > 0;
    const hasCurrentColumns = clientState?.columnState?.length > 0;
    const groupFunctionsChanged =
      currentGroupFunctions?.join('') !== previousGroupFunctions?.join('');

    if (hasPrevColumns && hasCurrentColumns && groupFunctionsChanged) {
      agGrid.api.refreshServerSide();
    }

    prevColumnStateRef.current = clientState.columnState;
  }, [clientState.columnState]);

  useEffect(() => {
    if (shouldOpenModalExceedsLimits()) {
      dispatch(
        showModal({
          name: MODAL_GENERAL_UPGRADE,
          props: { premiumFeature: 'fileSize' },
        })
      );
    }
  }, [currentFile, user]);

  const isMobileExperienceActive = localStorage.getItem('isMobileExperience') === 'true';

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    if (!agGrid || (!runDeletingRows && !isInsertingNewColumn)) {
      agGrid?.api?.refreshServerSide();
      return;
    }
    if (isInsertingNewColumn) return;

    generateDeletingRowsArray(agGrid).then((rows) => {
      if (clientState.alwaysConfirmDeleteRow) {
        return queueMicrotask(() =>
          flushSync(() =>
            dispatch(showModal({ name: MODAL_DELETE_ROWS, props: { deletingRows: rows } }))
          )
        );
      } else {
        handleDeleteRows(rows);
      }
    });
  }, [agGrid, runDeletingRows]);

  useEffect(() => {
    if (getGridRequestData) {
      document.addEventListener('keydown', handleKeyDown, true);
      return () => {
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    }
  }, [getGridRequestData]);

  useEffect(() => {
    if (screenWidth > 768) {
      queueMicrotask(() => flushSync(() => dispatch(setIsMobileSheetAssistantOpen(false))));
    }
  }, [screenWidth]);

  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  const shouldOpenModalExceedsLimits = () =>
    !isGigasheetOwner(currentFile) &&
    currentFile?.isFileShared &&
    currentFile?.metadata?.Owner !== user?.email &&
    !currentFile?.WithinQuota;

  const shouldOpenModalUpgradeAccount = () => {
    return (
      !isGigasheetOwner(currentFile) &&
      filesSizePercentage >= 100 &&
      !isLeftSidebarOpened &&
      prevFilesSizePercentage !== filesSizePercentage &&
      currentFile?.WithinQuota
    );
  };

  const shouldOpenModalUpgradeRequired = () => {
    const isModalOpen = modalsQueue?.some((el) => el.name === MODAL_GENERAL_UPGRADE);
    return (
      !isGigasheetOwner(currentFile) &&
      isPreviewMode &&
      !currentFile?.isFileShared &&
      !isModalOpen &&
      currentFile?.metadata?.FileSize &&
      user?.userProperties?.maxStorageSize &&
      !userClosedUpgradeRequiredModal
    );
  };

  const isSharedFileHasNoWritePermission =
    !user?.is_anonymous && currentFile?.isFileShared && hasNoWritePermission(currentFile);

  const onCellEditing = async (event) => {
    if (
      event.valueChanged &&
      event.oldValue !== event.newValue &&
      (isSharedFileHasNoWritePermission ||
        user?.is_anonymous ||
        !currentFile?.WithinQuota ||
        currentFile?.OverRowQuota)
    ) {
      const showModalAndStopEditing = (modalName) => {
        dispatch(showModal({ name: modalName }));
        event.node.setDataValue(event.column.getId(), event.oldValue);
        gridRef?.current?.api.stopEditing();
      };

      if (isSharedFileHasNoWritePermission) {
        showModalAndStopEditing(MODAL_MAKE_COPY);
      } else if (user?.is_anonymous) {
        showModalAndStopEditing(MODAL_REQUIRE_ACCOUNT);
      } else if (isPreviewMode) {
        showPreviewModeUpsellModal();
        event.node.setDataValue(event.column.getId(), event.oldValue);
        gridRef?.current?.api.stopEditing();
      }
      return;
    }

    if (!event.valueChanged || event.oldValue === event.newValue) return;

    if (
      event.oldValue !== event.newValue &&
      event.newValue === '=' &&
      event.newValue.length === 1
    ) {
      event.node.setDataValue(event.column.getId(), event.oldValue);
    }

    const uuid = getFileUuidFromPath();
    const columnId = event.colDef.colId;
    const rowId = event.data.A;
    const newValue = event.newValue?.toString();
    let endpoint = '';
    let payload = {};
    const errorToastText = 'Failed to update cell value. Please try again later.';

    const updateCell = async () => {
      try {
        endpoint = `${URL_DATASET}/${uuid}/${columnId}/${rowId}`;
        payload = {
          value: newValue,
        };
        const updatedCell = await put(endpoint, payload);

        if (!updatedCell.Success) {
          ShowToast({
            type: 'error',
            text: errorToastText,
            endpoint: endpoint,
            payload: payload,
            fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
          });
          event.node.setDataValue(event.column.getId(), event.oldValue);
        }
      } catch (error) {
        ShowToast({
          type: 'error',
          text: errorToastText,
          endpoint: endpoint,
          payload: payload,
          errorContext: error,
          fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
        });
        event.node.setDataValue(event.column.getId(), event.oldValue);
      }
    };
    event.api.flashCells({
      rowNodes: [event.node],
      columns: [event.colDef.field],
      flashDuration: 500,
    });
    await updateCell();
  };

  const runFormulaBuilderFromEditingCell = (params) => {
    const editingValue = params.event.target.value;
    if (
      (params.event.ctrlKey || params.event.metaKey) &&
      (params.event.key === 'c' || params.event.key === 'v' || params.event.key === 'x') &&
      isPreviewMode
    ) {
      params.event.preventDefault();
      return showPreviewModeUpsellModal();
    }
    if (
      (params.event.ctrlKey || params.event.metaKey) &&
      (params.event.key === 'c' || params.event.key === 'v' || params.event.key === 'x') &&
      user?.is_anonymous
    ) {
      params.event.preventDefault();
      return dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
    }

    if (!editingValue && params.event.key === '=')
      return openFormulaPopover({ mode: 'formula', resetForm: true });

    if (editingValue?.length === 1 && editingValue?.startsWith('=')) {
      openFormulaPopover({ mode: 'formula', resetForm: true });
    }
  };

  const getProperMobileDrawerContent = () => {
    if (mobileDrawerContent === SHEET_ASSISTANT) {
      return { title: 'Sheet Assistant', content: <SheetAssistant /> };
    }
    if (mobileDrawerContent === COLUMN_MANAGER) {
      return {
        title: 'Columns',
        content: (
          <CustomColumnManager openDeleteColumnsPopup={() => {}} isMobileVersion agGrid={agGrid} />
        ),
      };
    }
  };

  const renderMobileDrawer = () => {
    const properMobileDrawerContent = getProperMobileDrawerContent();

    return (
      isMobileScreen &&
      isMobileExperienceActive && (
        <MobileDrawer
          title={properMobileDrawerContent?.title || ''}
          onClose={() => flushSync(() => dispatch(setProperMobileDrawerContent(null)))}
          isOpen={mobileDrawerContent}
        >
          {properMobileDrawerContent?.content || null}
        </MobileDrawer>
      )
    );
  };

  useKeyPress(' ', handleSpacebarPress);

  useAggregationUpdater(agGrid, isAggregationAllowed, isUpdatingModel, isInitialRender.current);

  const fetchEnrichmentCredits = async () => {
    if (user?.is_anonymous) return;

    try {
      const response = await get('user/enrichment-credits');
      if (!response?.Limit) {
        dispatch(setEnrichmentCredits({ Limit: 0, Used: 0 }));
      }
      dispatch(setEnrichmentCredits(response));
    } catch (error) {
      throw new Error(error);
    }
  };

  useEffect(() => {
    if (!agGrid) return;
    fetchEnrichmentCredits();
  }, [agGrid]);

  useEffect(() => {
    try {
      if (clientState?.columnState && currentFile?.metadata) {
        const headers = clientState.columnState
          .filter((column) => column.groupFunc)
          .map((column) => {
            const { colId } = column;
            const headerIndex = Object.keys(currentFile.metadata.HeaderToColumnMapping).find(
              (key) => currentFile.metadata.HeaderToColumnMapping[key] === colId
            );
            return {
              header: currentFile.metadata.Headers[headerIndex],
              groupFunc: column.groupFunc,
            };
          });
        const areHeadersEqual = (headers1, headers2) => {
          if (headers1.length !== headers2.length) {
            return false;
          }
          for (let i = 0; i < headers1.length; i++) {
            if (
              headers1[i].header !== headers2[i].header ||
              headers1[i].groupFunc !== headers2[i].groupFunc
            ) {
              return false;
            }
          }
          return true;
        };

        if (!areHeadersEqual(headers, headersWithGroupFunc)) {
          setHeadersWithGroupFunc(headers);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [clientState?.columnState, currentFile?.metadata]);

  useEffect(() => {
    return () => {
      debouncedSaveChartModel.cancel();
    };
  }, [debouncedSaveChartModel]);

  useEffect(() => {
    if (!agGrid) return;
    const headerEl = document.querySelector('.ag-header-cell-comp-wrapper');
    if (!headerEl) return;

    const blockContextMenu = (e) => {
      // Only block right-click if the target is within a group cell or a header cell.
      if (e.target.closest('.ag-header-group-cell') || e.target.closest('.ag-header-cell')) {
        e.stopPropagation();
      }
    };

    document.addEventListener('contextmenu', blockContextMenu, true);

    return () => {
      headerEl.removeEventListener('contextmenu', blockContextMenu, true);
    };
  }, [agGrid]);

  const accumulatedCopyPasteCellsValue = useRef([]);
  const accumulatedCutCellsValue = useRef([]);
  const fullClipboardData = useRef([]);

  const onPasteStart = () => {
    accumulatedCopyPasteCellsValue.current = [];
    return null;
  };

  const onPasteEnd = (params) => {
    const rowCount = params.api.getDisplayedRowCount();
    if (fullClipboardData.current.length > rowCount) return null;
    handlePasteOperation(accumulatedCopyPasteCellsValue.current);
  };

  const handleCutOperation = debounce((params) => {
    handlePasteOperation(accumulatedCutCellsValue.current, true);
  }, 300);

  const onCellValueChanged = (params) => {
    const rowCount = params.api.getDisplayedRowCount();
    if (fullClipboardData.current.length > rowCount) return;
    const {
      column: { colId },
      newValue,
      oldValue,
      data,
    } = params;
    accumulatedCopyPasteCellsValue.current.push({
      rowIndex: data.A,
      colId,
      newValue,
      oldValue,
      cellValues: data,
    });
    const isCutToClipboard = params?.source === 'clipboardService' && params?.newValue === null;
    if (isCutToClipboard) {
      accumulatedCutCellsValue.current.push({
        rowIndex: data.A,
        colId,
        newValue,
        oldValue,
        cellValues: data,
      });
      handleCutOperation(params);
    }
  };

  const extractUpdatedAndOriginalCells = (cells) => {
    const OLD_SUFFIX = 'OLD_value';

    const cellMap = cells.reduce((acc, cell) => {
      const { rowIndex, colId, newValue, oldValue } = cell;

      acc[rowIndex] = acc[rowIndex] || {};
      acc[rowIndex][colId] = newValue;
      acc[rowIndex][`${colId}${OLD_SUFFIX}`] = oldValue;

      return acc;
    }, {});

    const filteredCells = Object.values(cellMap).filter((row) => row !== null);

    const newStructuredCells = [];
    const oldStructuredCells = [];

    filteredCells.forEach((row) => {
      const newCell = {};
      const oldCell = {};

      for (const key in row) {
        if (key.endsWith(OLD_SUFFIX)) {
          oldCell[key] = row[key];
        } else {
          newCell[key] = row[key];
        }
      }

      newStructuredCells.push(newCell);
      oldStructuredCells.push(oldCell);
    });

    return [newStructuredCells, oldStructuredCells];
  };

  const handlePasteOperation = async (rawValues, isCutOperation = false) => {
    const release = await pasteMutex.acquire();
    let endpoint = '';
    let payload = {};
    let response = null;
    const fileAndFunction = '__FILE_AND_FUNCTION_NAME__';
    const errorToastText = `${isCutOperation ? 'Cut' : 'Paste'} failed. Please try again later.`;
    try {
      dispatch(setIsInsertingNewColumn(true));

      const [newCellsValue, oldCellsValue] = extractUpdatedAndOriginalCells(rawValues);
      const uuid = getFileUuidFromPath();
      const requestAgGrid = gridRef.current.api.getGridOption('serverSideDatasource')?.state;

      if (newCellsValue?.length > 0 && rawValues?.length > 0) {
        payload = createPayload(newCellsValue, requestAgGrid);
        endpoint = createUpsertUrl(uuid, rawValues[0]);

        response = await put(endpoint, payload);

        if (!response.Success) {
          ShowToast({
            type: TOAST_TYPE_ERROR,
            text: errorToastText,
            endpoint: endpoint,
            payload: payload,
            fileAndFunction: fileAndFunction,
            responseStatus: response?.status,
          });
        } else {
          if (isCutOperation) {
            accumulatedCutCellsValue.current = [];
          } else {
            showUndoToast(oldCellsValue, requestAgGrid, endpoint);
          }
        }
      }
    } catch (e) {
      ShowToast({
        type: TOAST_TYPE_ERROR,
        text: errorToastText,
        endpoint: endpoint,
        payload: payload,
        errorContext: e,
        fileAndFunction: fileAndFunction,
        responseStatus: response?.status,
      });
    } finally {
      dispatch(setIsInsertingNewColumn(false));
      release();
    }
  };

  const createPayload = (cellsValue, requestAgGrid) => {
    return {
      cells: cellsValue.filter((row) => row !== null).map((row) => Object.values(row)),
      requestAgGrid: requestAgGrid,
    };
  };

  const createUpsertUrl = (uuid, rawValue) => {
    return `${URL_DATASET}/${uuid}/${rawValue?.colId}/${rawValue?.rowIndex}/upsert-rows`;
  };

  const showUndoToast = (oldCellsValue, requestAgGrid, upsertUrl) => {
    const toastBody = ({ closeToast }) => (
      <div>
        <p>Paste successful!</p>
        {/* eslint-disable-next-line */}
        <p
          id='undo-paste-btn'
          className='hover:cursor-pointer text-ocean-blue-600 hover:underline'
          onClick={async () => {
            closeToast();
            const oldCellsPayload = createPayload(oldCellsValue, requestAgGrid);
            const resp = await put(upsertUrl, oldCellsPayload);
            if (resp.Success) {
              const isGrouped = clientState?.columnState?.some((e) => e.rowGroup);
              if (isGrouped) {
                return agGrid.api.refreshServerSide({ purge: true });
              }
              return agGrid.api.refreshServerSide();
            }
          }}
        >
          Undo?
        </p>
      </div>
    );

    ShowToast({ type: 'info', customBody: toastBody });
  };

  const CustomLoadingRenderer = useCallback(() => {
    return <>{isLiveQuery && <DotsLoader setIsRequestingRows={setIsRequestingRows} />}</>;
  }, [setIsRequestingRows]);

  const processDataFromClipboard = (params) => {
    const clipboardData = params.data;
    fullClipboardData.current = clipboardData;
    const rowCount = params.api.getDisplayedRowCount();
    if (clipboardData.length <= rowCount) return clipboardData;
    try {
      _processDataFromClipboard(params, clientState, gridRef, dispatch);
    } catch (error) {
      ShowToast({ type: 'error', text: 'Paste failed. Please try again later.' });
    }
    return;
  };

  if (errorInfo.error) {
    if (errorInfo.errorMessage === currentFileErrorTexts[StatusCodes.FORBIDDEN]) {
      return isIframe ? <WithoutPublicAccess /> : <AccessForm />;
    } else {
      return <div className='spreadsheet-access-fail'>{errorInfo.errorMessage}</div>;
    }
  }

  return (
    <>
      <div className='spreadsheet-header-container'>
        <MetaTag documentTitle={currentFile ? currentFile?.metadata?.FileName : 'Gigasheet'} />

        <div
          className={clsx(
            'spreadsheet-header',
            isMobileExperienceActive && 'md:flex',
            !isMobileExperienceActive && 'flex'
          )}
        >
          <ToolPanel
            getGridRequestData={getGridRequestData}
            freeUserExport={freeUserExport}
            firstRowAsHeader={firstRowAsHeader}
            headerAsFirstRow={headerAsFirstRow}
            chartData={chartData}
            agGrid={agGrid}
            navigationProps={navigationProps}
            autoSizeAll={autoSizeAll}
          />
        </div>
        <div
          id='dropdiv'
          className={clsx(
            'table-container',
            isMobileScreen && !isMobileExperienceActive && 'fixed'
          )}
        >
          <>
            <div
              id='myGrid'
              style={{
                height: '100%',
                width: '100%',
              }}
              className='ag-theme-alpine'
              ref={gridWrapperRef}
            >
              {isGridStateRestored ? (
                <>
                  {agGrid &&
                    !limitedAnalystMenu &&
                    isGridStateRestored &&
                    document.querySelector(QUERY_SELECTOR_FORMULA_PANEL) &&
                    createPortal(
                      <FormulaPopoverButton agGrid={agGrid} />,
                      document.querySelector(QUERY_SELECTOR_FORMULA_PANEL)
                    )}
                  <AgGridReact
                    ref={gridRef}
                    icons={icons}
                    sideBar={sidebar}
                    getRowId={getRowId}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    autoGroupColumnDef={autoGroupColumnDef}
                    /* listeners */
                    onPasteEnd={onPasteEnd}
                    onGridReady={onGridReady}
                    onCellClicked={onCellClick}
                    onPasteStart={onPasteStart}
                    processDataFromClipboard={processDataFromClipboard}
                    onCellFocused={onCellFocus}
                    onDragStopped={onDragStopped}
                    onModelUpdated={onModelUpdated}
                    onFirstDataRendered={onFirstDataRendered}
                    onColumnVisible={onColumnVisible}
                    onColumnRowGroupChanged={onGroup}
                    onColumnPivotModeChanged={onPivot}
                    onColumnValueChanged={onAggregate}
                    onRowGroupOpened={onRowGroupOpened}
                    onCellValueChanged={onCellValueChanged}
                    onNewColumnsLoaded={onNewColumnsLoaded}
                    onPaginationChanged={onPaginationChanged}
                    onColumnPivotChanged={onAddToColumnLabels}
                    onColumnMoved={onColumnMoved}
                    enterNavigatesVertically={true}
                    enterNavigatesVerticallyAfterEdit={true}
                    onGridColumnsChanged={onColumnRenamed}
                    onCellEditingStopped={onCellEditing}
                    onChartCreated={onChartCreated}
                    onChartRangeSelectionChanged={onChartRangeSelectionChanged}
                    onChartOptionsChanged={onChartOptionsChanged}
                    onChartDestroyed={onChartDestroyed}
                    onToolPanelVisibleChanged={onToolPanelVisibleChanged}
                    /* listeners */
                    /* getters */
                    rowClassRules={getRowClassRules()}
                    getMainMenuItems={getMainMenuItems}
                    getContextMenuItems={getContextMenuItems}
                    getRowHeight={(params) => (params.node.group ? 36 : 24)}
                    postProcessPopup={(params) => columnMenuStyling(params)}
                    getChildCount={(data) => (data?.groupCount ? data.groupCount : 0)}
                    /* getters */
                    cacheBlockSize={paginationPageSize}
                    paginationPageSize={paginationPageSize}
                    /* getters */
                    /* components */
                    components={{
                      ...defaultFC,
                      customLoadingCellRenderer: CustomLoadingRenderer,
                    }}
                    /* components */
                    /* modifiers */
                    rowSelection='single'
                    rowModelType='serverSide'
                    rowGroupPanelShow='onlyWhenGrouping'
                    noRowsOverlayComponent='customNoRowsOverlay'
                    loadingCellRenderer='customLoadingCellRenderer'
                    suppressClipboardApi={true}
                    suppressColumnVirtualisation={columnDefs.length < 50}
                    maxConcurrentDatasourceRequests={1}
                    pagination
                    animateRows
                    scrollbarWidth={12}
                    suppressCutToClipboard={
                      sharedFileWithPermission_READ ||
                      !currentFile?.WithinQuota ||
                      currentFile?.OverRowQuota ||
                      isPreviewMode ||
                      user?.is_anonymous ||
                      limitedAnalystMenu
                    }
                    suppressMovableColumns={
                      hasNoWritePermission(currentFile) ||
                      !currentFile?.WithinQuota ||
                      currentFile?.OverRowQuota
                    }
                    enableCharts={
                      !isPreviewMode && !sharedFileWithPermission_READ && currentFile?.WithinQuota
                    }
                    paginateChildRows={false}
                    cellSelection
                    suppressAggFuncInHeader
                    suppressFieldDotNotation
                    enableCellEditingOnBackspace
                    onCellKeyDown={runFormulaBuilderFromEditingCell}
                    suppressCopySingleCellRanges={
                      isPreviewMode || user?.is_anonymous || limitedAnalystMenu ? true : false
                    }
                    suppressClipboardPaste={
                      isPreviewMode || user?.is_anonymous || limitedAnalystMenu ? true : false
                    }
                    /* modifiers */
                    modules={modules}
                  />
                  {!isLiveQuery && filterProgressIsRunning && (
                    <FilterProgressOverlay
                      isLiveQuery={isLiveQuery}
                      filterProgressIsRunning={filterProgressIsRunning}
                      progress={progress}
                      formatProgressLine={formatProgressLine}
                      totalNumberOfRows={totalNumberOfRows}
                      currentFile={currentFile}
                    />
                  )}

                  {isRenamePopoverOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        display: renamePopoverCoordinates ? 'block' : 'none',
                        ...renamePopoverCoordinates,
                      }}
                    >
                      <RenameColumnPopover />
                    </div>
                  )}
                  {isCustomColumnHeaderContextMenuOpen && (
                    <CustomColumnHeaderContextMenu
                      agGrid={agGrid}
                      position={customColumnHeaderContextMenuPosition}
                      customContextMenuColumn={customContextMenuColumn}
                    />
                  )}
                </>
              ) : (
                <SheetLoader />
              )}
            </div>
            <span
              className={clsx(
                'border-t border-ui-200 h-9',
                isMobileExperienceActive && 'hidden md:flex',
                !isMobileExperienceActive && 'flex'
              )}
            >
              <CustomPagination
                agGrid={agGrid}
                goToPage={goToPage}
                pageNumber={pageNumber}
                totalNumber={totalNumberOfRows}
                totalNumberOfPages={Math.ceil(
                  (totalGroupCount > 0 ? totalGroupCount : filteredNumberOfRows) /
                    paginationPageSize
                )}
              />
            </span>
            <span
              className={clsx(
                isMobileExperienceActive && 'flex flex-col md:hidden',
                !isMobileExperienceActive && 'hidden'
              )}
            >
              <MobilePagination
                goToPage={goToPage}
                pageNumber={pageNumber}
                totalNumber={totalNumberOfRows}
                totalNumberOfPages={Math.ceil(
                  (totalGroupCount > 0 ? totalGroupCount : filteredNumberOfRows) /
                    paginationPageSize
                )}
              />
            </span>
            {renderMobileDrawer()}
          </>
        </div>
      </div>
    </>
  );
};

export default SpreadsheetPage;
